import React, { useMemo, useRef, useState, useEffect } from 'react';
import '../styles/TradingSetupPanel.css';
import { computeConfluence } from '../utils/confluenceEngine';

// Intraday strike rule — mirrors TradingSignalService.selectEntryStrike: ATM or one strike ITM,
// whichever has |delta| in 0.50–0.60 (nearest 0.55); ATM otherwise. The old DTE > 7 rule picked
// 0.25–0.48Δ OTM on 32-day options — vega-heavy, low delta, and the spread ate the move.
const STRIKE_MIN_DELTA = 0.50;
const STRIKE_MAX_DELTA = 0.60;
// Theta is judged over this intraday hold (mirrors riskplan.theta-hold-minutes).
const THETA_HOLD_MINUTES = 60;

function recommendStrike(atm, itm, deltaKey) {
  const mid = (STRIKE_MIN_DELTA + STRIKE_MAX_DELTA) / 2;
  let best = 'ATM', bestGap = Infinity, anyInBand = false;
  [['ATM', atm], ['ITM', itm]].forEach(([key, s]) => {
    const d = s?.[deltaKey] != null ? Math.abs(s[deltaKey]) : null;
    if (d == null || d < STRIKE_MIN_DELTA || d > STRIKE_MAX_DELTA) return;
    if (Math.abs(d - mid) < bestGap) { bestGap = Math.abs(d - mid); best = key; anyInBand = true; }
  });
  return anyInBand ? best : 'ATM';
}

// Circuit breaker (enforced by the backend SYSTEM engine, displayed here): after 2 losses,
// trading is locked for a mandatory cooldown, then resumes only after a chain sweep holds for
// several consecutive polls in the direction about to be traded.
const CB_COOLDOWN_MS       = 15 * 60 * 1000; // 15 minutes
const SWEEP_CONFIRM_POLLS  = 3;              // consecutive polls a sweep must hold to count


// DTE-adaptive trailing stop percentage.
// Weekly options have high gamma — a flat 30% gives back too much on short-dated options.
// Returns the DROP fraction (e.g. 0.20 = exit when LTP falls 20% from peak).
const dteSl = (dte) => {
  if (dte == null || dte > 7) return 0.30; // 8+ DTE (monthly)  — 30%
  if (dte >= 3)               return 0.25; // 3–7 DTE (weekly)  — 25%
  if (dte >= 1)               return 0.20; // 1–2 DTE           — 20%
  return 0.15;                             // 0DTE              — 15%
};

// Computes a simple least-squares linear slope across up to the last 5 PCR values.
// Returns slope in PCR-units per poll interval (positive = rising = bullish, negative = falling = bearish).
// Returns 0 when fewer than 3 data points are available.
function computePcrSlope(history) {
  const pts = history.slice(-5);
  if (pts.length < 3) return 0;
  const n     = pts.length;
  const meanX = (n - 1) / 2;
  const meanY = pts.reduce((s, v) => s + v, 0) / n;
  let ssXX = 0, ssXY = 0;
  pts.forEach((y, x) => {
    ssXX += (x - meanX) ** 2;
    ssXY += (x - meanX) * (y - meanY);
  });
  return ssXX === 0 ? 0 : ssXY / ssXX;
}

// Returns the OI matrix classification that appears ≥2 times in the last 3 polls, or null if no consensus.
// Prevents single-poll HFT blips from changing the classification.
function confirmMatrix(history, key) {
  if (!history || history.length < 2) return null;
  const counts = {};
  history.forEach(h => { const v = h[key]; if (v) counts[v] = (counts[v] || 0) + 1; });
  const top = Object.entries(counts).sort((a, b) => b[1] - a[1])[0];
  return top && top[1] >= 2 ? top[0] : null;
}

// Formats a matrix classification for display, appending confirmation status.
const matrixLabel = (raw, confirmed) =>
  confirmed ? `${raw} ✓` : raw ? `${raw} (unconfirmed)` : 'N/A';

const TradingSetupPanel = ({
  analysis, gammaData, emaData, vixDirection,
  maxPainPrice, pcrHistory = [], support, resistance,
  symbol,
  expiryDate,
  configStrikePrice,
  sessionTrades = { wins: 0, losses: 0 },
  onMarkWin, onMarkLoss, onResetSession,
  onRecordSignal,
  onSquareOff,
  onVerdictChange,
  onCircuitBreaker,
  tradeHistory = [],
  oiMatrixHistory = [],
  gammaFlipCrossover = null,
  leadingStocksData = null,
  backendVerdict = null,
  onRiskLots = 1,
  takeTradeLots = 2,
  backendSystemStatus = null,
  onLiveTradeChange,
}) => {
  // shouldRender guards ALL rendering — hooks must still run unconditionally
  const shouldRender = !!(analysis && gammaData && gammaData.hasValidData);

  // tfBias declared early — used in finalVerdict chain (before MTF section)
  const tfBias        = emaData?.tfBias;   // BUY_CE | BUY_PE | AVOID | NOT_READY | null
  const sidewaysBlock = tfBias === 'AVOID';

  // ── Adaptive win-rate stats ────────────────────────────────────────────────
  const histStats = useMemo(() => {
    if (!tradeHistory || tradeHistory.length === 0 || !symbol) return null;
    const closed = tradeHistory.filter(t => t.symbol === symbol && t.outcome !== 'OPEN');
    if (closed.length < 5) return null;

    const wins  = closed.filter(t => t.outcome === 'WIN').length;
    const wr    = Math.round(wins / closed.length * 100);

    // Manual trades are excluded here even when their finalVerdict mirrors TAKE TRADE/ON
    // RISK — this stat feeds the adaptive downgrade gate below, which must reflect the
    // SYSTEM's own signal performance, not the trader's discretionary manual entries.
    const ttTrades = closed.filter(t => t.finalVerdict === 'TAKE TRADE' && !t.isManual);
    const ttWins   = ttTrades.filter(t => t.outcome === 'WIN').length;
    const ttWR     = ttTrades.length > 0 ? Math.round(ttWins / ttTrades.length * 100) : null;

    const orTrades = closed.filter(t => t.finalVerdict === 'ON RISK' && !t.isManual);
    const orWins   = orTrades.filter(t => t.outcome === 'WIN').length;
    const orWR     = orTrades.length > 0 ? Math.round(orWins / orTrades.length * 100) : null;

    // 5/20 EMA trend trigger entries (backend EmaTrend) — a separate strategy, shown on its own.
    const emaTrades = closed.filter(t => t.finalVerdict === 'EMA TREND' && !t.isManual);
    const emaWins   = emaTrades.filter(t => t.outcome === 'WIN').length;
    const emaWR     = emaTrades.length > 0 ? Math.round(emaWins / emaTrades.length * 100) : null;

    const withPnl  = closed.filter(t => t.pnlPts != null);
    const avgPnl   = withPnl.length > 0
      ? parseFloat((withPnl.reduce((s, t) => s + t.pnlPts, 0) / withPnl.length).toFixed(1))
      : null;

    return { total: closed.length, wins, wr, ttCount: ttTrades.length, ttWins, ttWR, orCount: orTrades.length, orWins, orWR, emaCount: emaTrades.length, emaWins, emaWR, avgPnl };
  }, [tradeHistory, symbol]);

  // Today's closed MANUAL trades on this symbol — shown beside the SYSTEM W/L in the header.
  // Display only: the circuit breaker stays on the backend SYSTEM count (sessionTrades).
  const manualToday = useMemo(() => {
    if (!tradeHistory?.length || !symbol) return { wins: 0, losses: 0 };
    const today = new Date().toDateString();
    const closed = tradeHistory.filter(t => t.isManual && t.symbol === symbol && t.outcome !== 'OPEN'
      && new Date(t.timestamp).toDateString() === today);
    return {
      wins:   closed.filter(t => t.outcome === 'WIN').length,
      losses: closed.filter(t => t.outcome === 'LOSS').length,
    };
  }, [tradeHistory, symbol]);

  // Auto-trade tracking refs — useRef/useState must be unconditional (Rules of Hooks)
  const autoTradeRef         = useRef(null);   // holds the currently auto-recorded open trade
  // Post-square-off cooldown: blocks any auto-re-entry (either direction) for 3 polls (~1-2 min)
  // Circuit-breaker episode: tracks the mandatory 15-min lockout after 2 losses, and whether
  // it has since been released by a persistently-confirmed chain sweep (see below).
  // Consecutive-poll counters for chain sweep — a single poll at ≥40% is easy to hit on noise;
  // requiring it to hold for several polls is what "judging the sweep correctly" means here.
  // Rolling session ATM-IV window for the dynamic condition-7 threshold — mirrors
  // TradingSignalService.java's sessionAtmIv map (last 50 readings, per-symbol there;
  // a single unkeyed ref suffices here since one mounted cockpit only ever tracks one symbol).
  const ivHistoryRef         = useRef([]);
  const [isAutoTracking,  setIsAutoTracking]  = useState(false);

  // Seed autoTradeRef from an overnight OPEN trade so trailing SL resumes across sessions.
  // Runs whenever tradeHistory loads (backend fetch) or symbol changes.
  // Guard: skip if already tracking a trade for this symbol.
  useEffect(() => {
    if (autoTradeRef.current?.symbol === symbol) return; // already tracking this symbol
    if (!symbol || !tradeHistory?.length) return;

    const openTrade = tradeHistory
      // Multi-leg selling structures (t.strategy) are managed by the backend StrategyEngine —
      // their entryLTP is a net credit, not a premium to trail.
      .filter(t => t.symbol === symbol && t.outcome === 'OPEN' && t.entryLTP != null && !t.strategy)
      .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))[0];

    if (!openTrade) return;

    autoTradeRef.current = {
      ...openTrade,
      // peakLTP = entryLTP: trailing SL starts from entry — ratchets up as today's price moves
      peakLTP: openTrade.entryLTP,
      slPct:   openTrade.slPct ?? dteSl(openTrade.dte),
    };
    setIsAutoTracking(true);
  }, [tradeHistory, symbol]); // eslint-disable-line react-hooks/exhaustive-deps

  // Lift the live-trade flag up to AnalyzerPage.js — used to lock the global expiry/strike
  // dropdowns (MenuBar.js) while a trade is open. Changing either mid-trade would make
  // fetchAnalysis poll a different option chain than the one the open trade's LTP lookup
  // needs, silently corrupting its trailing-SL/P&L calculation.
  useEffect(() => {
    onLiveTradeChange?.(isAutoTracking ? autoTradeRef.current : null);
  }, [isAutoTracking]); // eslint-disable-line react-hooks/exhaustive-deps

  const [showCandleSteps,  setShowCandleSteps]  = useState(false); // candle rule detail toggle
  const [showExpiryDetail, setShowExpiryDetail] = useState(false); // expiry guide detail toggle
  const [showStrikeDetail, setShowStrikeDetail] = useState(false); // strike table detail toggle
  const [showAllReasons,   setShowAllReasons]   = useState(false); // NO TRADE reasons overflow

  // Safe destructuring — returns undefined when data is absent; all conditions evaluate to false
  const {
    oiSetupSignal,
    ceOIMatrix,
    peOIMatrix,
    atmCallIV,
    atmPutIV,
    ivRegime,
    // Full-chain PCR label — "Over-Bearish" (PCR<0.6) or "Over-Bullish" (PCR>1.4) signal extreme crowd positioning
    totalOI,
  } = analysis ?? {};

  const {
    bbSignal,
    volumeAlerts: volumeAlertsRaw,
    gammaBlastZone,
    daysToExpiry,
    ivRank,
    ivPercentile,
    straddleExpansionRatio,
    gapOpenPct,
    timeZone,
    buyerFriendlyScore,
    spotPrice,
    strikeData: strikeDataRaw,
    atmStrike,
    atmCallTheta,
    atmPutTheta,
    atmCallLTP,
    atmPutLTP,
    thetaCallPct,
    thetaPutPct,
    futuresTradeSignal,
    futuresBasisState,
    futuresBasisPct,
  } = gammaData ?? {};
  // Backend may return explicit null for arrays — ?? [] handles both null and undefined
  const volumeAlerts = volumeAlertsRaw ?? [];
  const strikeData   = strikeDataRaw   ?? [];

  // ATM premium edge: how much the live LTP exceeds the Black-Scholes theoretical price (%).
  // Positive = option trading above fair value (overpriced — buyer pays a spread premium).
  // Negative = option trading below fair value (discount — rare but favourable for buyers).
  // Null when no LTP data or no theoretical price (illiquid strike or first polls).
  const atmStrikeData = atmStrike != null
    ? strikeData.find(s => s.strikePrice === atmStrike) ?? null
    : null;
  const atmCallEdge = atmStrikeData?.callPremiumEdge ?? null;
  const atmPutEdge  = atmStrikeData?.putPremiumEdge  ?? null;

  // LTP lookup: look up a specific strike in strikeData, fall back to ATM.
  // Uses the explicitly provided strikeNum when given (active-trade tracking),
  // then configStrikePrice (user's LoginPage selection), then ATM LTP.
  const getStrikeLTP = (direction, strikeNum) => {
    const target = strikeNum || (configStrikePrice > 0 ? configStrikePrice : null);
    if (target && strikeData.length > 0) {
      const match = strikeData.find(s => s.strikePrice === Number(target));
      if (match) return direction === 'call' ? (match.ceLTP ?? null) : (match.peLTP ?? null);
    }
    return direction === 'call' ? (atmCallLTP ?? null) : (atmPutLTP ?? null);
  };

  // ── OI matrix 2-of-3 poll confirmation ────────────────────────────────────
  // A classification is "confirmed" only when the same value appears in ≥2 of the last 3 polls.
  // Falls back to the current poll's raw value until enough history accumulates.
  const confirmedCeOIMatrix = confirmMatrix(oiMatrixHistory, 'ce');
  const confirmedPeOIMatrix = confirmMatrix(oiMatrixHistory, 'pe');
  const effectiveCeOIMatrix = confirmedCeOIMatrix ?? ceOIMatrix;
  const effectivePeOIMatrix = confirmedPeOIMatrix ?? peOIMatrix;

  const hasVolSpike    = volumeAlerts.length > 0;
  // Direction-aligned volume: only count spikes that match the trade direction
  const volCallAligned = volumeAlerts.some(a => a.sentiment === 'BULLISH');
  const volPutAligned  = volumeAlerts.some(a => a.sentiment === 'BEARISH');
  // Spike counts — used to distinguish ON RISK (≥1) from TAKE TRADE quality (≥2)
  const bullishSpikeCount = volumeAlerts.filter(a => a.sentiment === 'BULLISH').length;
  const bearishSpikeCount = volumeAlerts.filter(a => a.sentiment === 'BEARISH').length;

  // ── Chain-wide OI sweep (breadth signal) ──────────────────────────────────
  // Aggregates per-strike cePremiumSignal / pePremiumSignal across the full option chain.
  // When the same directional pattern repeats across ≥40% of active strikes it signals
  // institutional conviction — far stronger than a single ATM OI matrix reading.
  //
  // BULLISH SWEEP  → CE Long Build-up + PE Short Build-up at many strikes
  //   Call buyers aggressively entering across the chain + put writers adding protection
  //   = market expects a big upward move.
  // BEARISH SWEEP  → PE Long Build-up + CE Short Build-up at many strikes
  //   Put buyers aggressively entering + call writers adding = big downward move expected.
  // A pure percentage is backwards on a thin chain: active.length can be as low as 3 on the
  // least liquid symbols (BANKEX), where just 2 strikes randomly agreeing already clears 40%
  // — the symbol that most needs a stricter bar gets the noisiest one. MIN_ACTIVE_COUNT raises
  // the denominator floor, MIN_AGREEING_COUNT requires a real absolute number of agreeing
  // strikes on top of the percentage (mirrors GammaService.java's chain-sweep detection).
  const CHAIN_SWEEP_MIN_ACTIVE   = 6;
  const CHAIN_SWEEP_MIN_AGREEING = 3;
  const chainSweepData = (() => {
    const active = strikeData.filter(s => s.cePremiumSignal && s.pePremiumSignal);
    if (active.length < CHAIN_SWEEP_MIN_ACTIVE) return { bullPct: 0, bearPct: 0, bullCount: 0, bearCount: 0, total: 0 };
    const bullCount = active.filter(s =>
      s.cePremiumSignal === 'Long Build-up' && s.pePremiumSignal === 'Short Build-up'
    ).length;
    const bearCount = active.filter(s =>
      s.pePremiumSignal === 'Long Build-up' && s.cePremiumSignal === 'Short Build-up'
    ).length;
    return {
      bullCount, bearCount, total: active.length,
      bullPct: Math.round(bullCount / active.length * 100),
      bearPct: Math.round(bearCount / active.length * 100),
    };
  })();
  const chainSweepBull = chainSweepData.bullPct >= 40 && chainSweepData.bullCount >= CHAIN_SWEEP_MIN_AGREEING;
  const chainSweepBear = chainSweepData.bearPct >= 40 && chainSweepData.bearCount >= CHAIN_SWEEP_MIN_AGREEING;
  // Dynamic IV threshold (#7) — ported from TradingSignalService.java's dynamicIVThreshold so
  // the cockpit's checklist matches what the backend SYSTEM engine actually scores condition 7
  // against (previously a different static min(30,max(15,avgIV*0.85)) formula here vs. the
  // backend's session-relative percentile — the two could disagree on whether condition 7
  // passed for the identical market data). Threshold = 70th percentile of today's own ATM-IV
  // readings (avg of call+put when both available, last 50 kept); falls back to a flat 20
  // until 5 readings have accumulated this session.
  const sessionAtmIv = (atmCallIV != null && atmPutIV != null) ? (atmCallIV + atmPutIV) / 2
                      : atmCallIV != null ? atmCallIV : atmPutIV;
  useEffect(() => {
    if (sessionAtmIv != null && sessionAtmIv > 0) {
      ivHistoryRef.current = [...ivHistoryRef.current, sessionAtmIv].slice(-50);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionAtmIv]);
  const ivThreshold = (() => {
    const buf = ivHistoryRef.current;
    if (buf.length < 5) return 20;
    const sorted = [...buf].sort((a, b) => a - b);
    const p70idx = Math.ceil(sorted.length * 0.70) - 1;
    return sorted[Math.max(0, p70idx)];
  })();

  // PCR trend: linear slope across last 5 polls.
  // Positive = rising PCR (put OI growing relative to calls = put writers adding support = bullish).
  // Negative = falling PCR (put OI shrinking or calls surging = bearish).
  const pcrTrend = computePcrSlope(pcrHistory);

  const { confluenceScore, trendStrength, earlyTrendDirection, noTradeReasons } =
    computeConfluence(analysis, gammaData, emaData, vixDirection, pcrTrend, gammaFlipCrossover, leadingStocksData);

  // ── 6-condition checklists ─────────────────────────────────────────────────
  // VWAP setup score from EmaData (computed in EmaService)
  const callVwapScore = emaData?.callVwapScore ?? 0;
  const putVwapScore  = emaData?.putVwapScore  ?? 0;
  const vwapSetup     = emaData?.vwapSetupSignal;   // "CALL_SETUP" | "PUT_SETUP" | "WATCH" | "NEUTRAL"
  const vwapReady     = emaData?.vwap != null;
  const vwapDetail    = vwapReady
    ? `VWAP ${emaData.vwap.toFixed(0)} · ${emaData.priceAboveVwap ? 'Above' : 'Below'} · slope ${emaData.vwapSlopingUp ? '↗' : emaData.vwapSlopingDown ? '↘' : '→'} · ${callVwapScore}/${putVwapScore} C/P`
    : "Collecting today\u2019s bars\u2026";

  // ── Lead stock alignment (condition 8) ───────────────────────────────────
  // Only applicable when lead stocks data is present for this index.
  // "aligned" = majority of heavyweight stocks moving in the same direction as the index signal.
  // "opposing" = heavyweights moving against the index direction (early warning to stay out).
  const hasLeadData   = leadingStocksData && leadingStocksData.stocks && leadingStocksData.stocks.length > 0;
  const leadBullish   = hasLeadData && leadingStocksData.compositeBias === 'BULLISH';
  const leadBearish   = hasLeadData && leadingStocksData.compositeBias === 'BEARISH';
  // Missing lead data scores 0 on both sides — same rule as the backend (TradingSignalService).
  const leadCallMet   = leadBullish;
  const leadPutMet    = leadBearish;

  // Per-stock detail string for checklist tooltip
  const leadCallDetail = hasLeadData
    ? `${leadingStocksData.compositeBias} · ${leadingStocksData.leadStrength} · ${leadingStocksData.alignmentSummary}`
    : 'No lead data (non-index)';
  const leadPutDetail = leadCallDetail;

  // Hard block: opposing STRONG or MODERATE lead downgrades TAKE TRADE → ON RISK
  // (WEAK opposing is only a warning, not a block — index can diverge from stocks briefly)
  const leadStockOpposingCall = hasLeadData && leadBearish
    && (leadingStocksData.leadStrength === 'STRONG' || leadingStocksData.leadStrength === 'MODERATE');
  const leadStockOpposingPut  = hasLeadData && leadBullish
    && (leadingStocksData.leadStrength === 'STRONG' || leadingStocksData.leadStrength === 'MODERATE');

  const callConditions = [
    { label: 'Index above BB Mid',
      met: bbSignal === 'Bullish',
      detail: `BB Signal: ${bbSignal ?? 'N/A'}` },
    { label: 'OI setup bullish',
      met: oiSetupSignal === 'Bullish' || oiSetupSignal === 'Strong Bullish',
      detail: oiSetupSignal || 'N/A' },
    // CE Long Build-up (buyers entering) is equally bullish as Short Covering (writers exiting)
    { label: 'CE OI bullish (LB or SC)',
      met: effectiveCeOIMatrix === 'Short Covering' || effectiveCeOIMatrix === 'Long Build-up',
      detail: `CE: ${matrixLabel(ceOIMatrix, confirmedCeOIMatrix)}` },
    // PE Short Build-up (writers adding) OR Long Unwinding (put buyers exiting) — both confirm bullish underlying
    { label: 'Put OI confirming (SB or LU)',
      met: effectivePeOIMatrix === 'Short Build-up' || effectivePeOIMatrix === 'Long Unwinding',
      detail: `PE: ${matrixLabel(peOIMatrix, confirmedPeOIMatrix)}` },
    // Chain sweep qualifies alongside a volume spike — institutional breadth = strong signal
    { label: 'Bullish vol or chain sweep',
      met: volCallAligned || chainSweepBull,
      detail: chainSweepBull
        ? `Chain sweep ${chainSweepData.bullPct}% (${chainSweepData.bullCount}/${chainSweepData.total} strikes)`
        : volCallAligned
          ? `${volumeAlerts.filter(a => a.sentiment === 'BULLISH').length} bullish spike(s)`
          : hasVolSpike ? `${volumeAlerts.length} spike(s) — none bullish` : 'No sweep / spikes' },
    { label: 'Low-moderate IV',
      met: atmCallIV != null && atmCallIV < ivThreshold,
      detail: atmCallIV != null ? `CE IV: ${atmCallIV.toFixed(1)}%` : 'N/A' },
    // Step 6: VWAP — price above + sloping up + at least pullback or breakout pressure
    { label: 'VWAP call setup (≥2/4)',
      met: callVwapScore >= 2,
      detail: vwapDetail },
    // Step 8: Lead stocks — heavyweight constituents moving in the same direction
    { label: 'Lead stocks bullish',
      met: leadCallMet,
      detail: leadCallDetail },
    // Step 9: Synthetic futures (put-call parity) — premium confirms institutional long positioning
    { label: 'Futures premium (bullish)',
      met: futuresTradeSignal === 'BUY CE',
      detail: futuresTradeSignal != null
        ? `F-Basis: ${futuresBasisState ?? '—'}${futuresBasisPct != null ? ` ${futuresBasisPct > 0 ? '+' : ''}${futuresBasisPct.toFixed(3)}%` : ''}`
        : 'Futures N/A' },
    // Condition 10: ATM CE not overpriced vs Black-Scholes fair value.
    // A positive edge > 10% means market makers have widened the spread — typically a pre-event
    // IV spike or an illiquid period (early morning, pre-close). Entering here means you are paying
    // significantly more than the theoretical option value; the excess burns off as vol-crush.
    // Null-safe: passes when fair value is unavailable (no LTP data / first polls after startup).
    { label: 'CE premium fair (edge ≤10%)',
      met: atmCallEdge != null && atmCallEdge <= 10,
      detail: atmCallEdge != null
        ? `CE edge: ${atmCallEdge > 0 ? '+' : ''}${atmCallEdge.toFixed(1)}% vs fair value` +
          (atmCallEdge > 10 ? ' — overpriced' : atmCallEdge < 0 ? ' — discount ✓' : '')
        : 'BS fair value N/A' },
  ];

  const putConditions = [
    { label: 'Index below BB Mid',
      met: bbSignal === 'Bearish',
      detail: `BB Signal: ${bbSignal ?? 'N/A'}` },
    { label: 'OI setup bearish',
      met: oiSetupSignal === 'Bearish' || oiSetupSignal === 'Strong Bearish',
      detail: oiSetupSignal || 'N/A' },
    // PE Long Build-up (put buyers entering) is equally bearish as Short Covering (put writers exiting)
    { label: 'PE OI bearish (LB or SC)',
      met: effectivePeOIMatrix === 'Short Covering' || effectivePeOIMatrix === 'Long Build-up',
      detail: `PE: ${matrixLabel(peOIMatrix, confirmedPeOIMatrix)}` },
    // CE Short Build-up (writers adding) OR Long Unwinding (call buyers exiting) — both confirm bearish underlying
    { label: 'Call OI confirming (SB or LU)',
      met: effectiveCeOIMatrix === 'Short Build-up' || effectiveCeOIMatrix === 'Long Unwinding',
      detail: `CE: ${matrixLabel(ceOIMatrix, confirmedCeOIMatrix)}` },
    { label: 'Bearish vol or chain sweep',
      met: volPutAligned || chainSweepBear,
      detail: chainSweepBear
        ? `Chain sweep ${chainSweepData.bearPct}% (${chainSweepData.bearCount}/${chainSweepData.total} strikes)`
        : volPutAligned
          ? `${volumeAlerts.filter(a => a.sentiment === 'BEARISH').length} bearish spike(s)`
          : hasVolSpike ? `${volumeAlerts.length} spike(s) — none bearish` : 'No sweep / spikes' },
    { label: 'Low-moderate IV',
      met: atmPutIV != null && atmPutIV < ivThreshold,
      detail: atmPutIV != null ? `PE IV: ${atmPutIV.toFixed(1)}%` : 'N/A' },
    // Step 6: VWAP — price below + sloping down + at least pullback or breakdown pressure
    { label: 'VWAP put setup (≥2/4)',
      met: putVwapScore >= 2,
      detail: vwapDetail },
    // Step 8: Lead stocks — heavyweight constituents moving in the same direction
    { label: 'Lead stocks bearish',
      met: leadPutMet,
      detail: leadPutDetail },
    // Step 9: Synthetic futures (put-call parity) — discount confirms institutional short positioning
    { label: 'Futures discount (bearish)',
      met: futuresTradeSignal === 'BUY PE',
      detail: futuresTradeSignal != null
        ? `F-Basis: ${futuresBasisState ?? '—'}${futuresBasisPct != null ? ` ${futuresBasisPct > 0 ? '+' : ''}${futuresBasisPct.toFixed(3)}%` : ''}`
        : 'Futures N/A' },
    // Condition 10: ATM PE not overpriced vs Black-Scholes fair value.
    { label: 'PE premium fair (edge ≤10%)',
      met: atmPutEdge != null && atmPutEdge <= 10,
      detail: atmPutEdge != null
        ? `PE edge: ${atmPutEdge > 0 ? '+' : ''}${atmPutEdge.toFixed(1)}% vs fair value` +
          (atmPutEdge > 10 ? ' — overpriced' : atmPutEdge < 0 ? ' — discount ✓' : '')
        : 'BS fair value N/A' },
  ];

  const callScore = callConditions.filter(c => c.met).length;
  const putScore  = putConditions.filter(c => c.met).length;
  const total     = callConditions.length; // 10

  // ── Direction & overall signal ─────────────────────────────────────────────
  // Scored out of the conditions that have data — same rule as the backend
  // (TradingSignalService): BUY = all but one evaluable condition, BIAS = all but two
  // (9/10 and 8/10 with full data); fewer than 8 evaluable → NO SETUP. The denominators come
  // from the backend verdict (it knows which feeds are warming up / missing); until the first
  // backend verdict for this symbol arrives, all 10 are assumed.
  const MIN_EVALUABLE = 8;
  const bvSameSymbol  = backendVerdict && backendVerdict.symbol === symbol;
  const callEval = bvSameSymbol && backendVerdict.callEvaluable != null ? backendVerdict.callEvaluable : total;
  const putEval  = bvSameSymbol && backendVerdict.putEvaluable  != null ? backendVerdict.putEvaluable  : total;
  const signal =
    callEval >= MIN_EVALUABLE && callScore >= callEval - 1 && callScore > putScore ? 'CALL BUY'  :
    putEval  >= MIN_EVALUABLE && putScore  >= putEval  - 1 && putScore > callScore  ? 'PUT BUY'   :
    callEval >= MIN_EVALUABLE && callScore >= callEval - 2 && callScore > putScore  ? 'CALL BIAS' :
    putEval  >= MIN_EVALUABLE && putScore  >= putEval  - 2 && putScore > callScore  ? 'PUT BIAS'  :
    'NO SETUP';

  const signalColor =
    signal === 'CALL BUY'  ? '#00c853' :
    signal === 'PUT BUY'   ? '#d32f2f' :
    signal === 'CALL BIAS' ? '#2e7d32' :
    signal === 'PUT BIAS'  ? '#c62828' :
    '#9e9e9e';

  const dir = callScore > putScore ? 'call' : putScore > callScore ? 'put' : null;

  const marketTrend = (() => {
    if (oiSetupSignal === 'Strong Bullish' && bbSignal === 'Bullish') return 'Strong Bullish Trend';
    if (oiSetupSignal === 'Bullish' && bbSignal === 'Bullish') return 'Bullish Trend';
    if (oiSetupSignal === 'Strong Bearish' && bbSignal === 'Bearish') return 'Strong Bearish Trend';
    if (oiSetupSignal === 'Bearish' && bbSignal === 'Bearish') return 'Bearish Trend';
    if (bbSignal === 'Bullish') return oiSetupSignal === 'Neutral' ? 'Bullish Trend (OI Neutral)' : 'Bullish Trend';
    if (bbSignal === 'Bearish') return oiSetupSignal === 'Neutral' ? 'Bearish Trend (OI Neutral)' : 'Bearish Trend';
    return 'No clear trend / Balanced market';
  })();

  // ── 4-Factor Entry Gate (direction-aware) ─────────────────────────────────
  // Factor 1 — Trend: requires Long Build-up (price↑ + OI↑) for TAKE TRADE.
  // Long Build-up = fresh buyers entering with conviction; the move has institutional backing and
  // is likely to sustain. Short Covering (price↑ + OI↓) = trapped writers exiting; once all
  // covering is done the buying pressure evaporates and the move can reverse sharply.
  // Short Covering still contributes to ON RISK via oiChangeMet (which accepts CE Short Covering
  // explicitly), so metCount stays at 3/4 and isOnRisk fires — the trade is allowed at half size.
  const trendMet =
    dir === 'call' ? effectiveCeOIMatrix === 'Long Build-up' :
    dir === 'put'  ? effectivePeOIMatrix === 'Long Build-up' :
    false;

  // Factor 2 — OI change confirming direction
  const oiChangeMet =
    dir === 'call' ? (effectiveCeOIMatrix === 'Short Covering' || effectivePeOIMatrix === 'Short Build-up') :
    dir === 'put'  ? (effectivePeOIMatrix === 'Short Covering' || effectiveCeOIMatrix === 'Short Build-up') :
    false;

  // Factor 3 — Volume aligned with trade direction, with two quality tiers:
  //   volumeMetPartial (≥1 aligned spike) — sufficient for ON RISK (half lot)
  //   volumeMetFull    (≥2 aligned spikes) — required for TAKE TRADE (full lot)
  // A single spike could be a one-off print; two independent strikes showing the same
  // directional sentiment is genuine institutional participation across the chain.
  const volumeMetPartial =
    dir === 'call' ? volCallAligned :
    dir === 'put'  ? volPutAligned  :
    hasVolSpike;
  const volumeMetFull =
    dir === 'call' ? bullishSpikeCount >= 2 :
    dir === 'put'  ? bearishSpikeCount >= 2 :
    volumeAlerts.length >= 2;
  // fourFactors display uses volumeMetPartial so the gate shows ✓ for 1 spike
  // (consistent with ON RISK routing); allFourMet is overridden below to use volumeMetFull.
  const volumeMet = volumeMetPartial;

  // Factor 4 — Premium environment favourable for buying.
  // Requires at least ONE observable signal that premiums are not collapsing:
  //   straddleExpansionRatio > 1.05 — straddle grown ≥5% since open (intraday expansion)
  //   ivRank < 50                   — IV below 60-day median (historically cheap, low vol-crush risk)
  // If neither is available → false (conservative — don't fire full size without evidence).
  // The former third OR (ivRegime !== 'Very Low') was removed: avgIV is ≥12 on every real
  // trading day so the condition was permanently satisfied and filtered nothing.
  const volExpMet =
    (straddleExpansionRatio != null && straddleExpansionRatio > 1.05) ||
    (ivRank != null && ivRank < 50);

  const fourFactors = [
    { key: 'trend',   label: 'Trend',      met: trendMet,
      detail: dir === 'call'
        ? `CE: ${matrixLabel(ceOIMatrix, confirmedCeOIMatrix)}${effectiveCeOIMatrix === 'Short Covering' ? ' — LB needed for TAKE TRADE' : ''}`
        : dir === 'put'
        ? `PE: ${matrixLabel(peOIMatrix, confirmedPeOIMatrix)}${effectivePeOIMatrix === 'Short Covering' ? ' — LB needed for TAKE TRADE' : ''}`
        : 'N/A' },
    { key: 'oi',      label: 'OI Change',  met: oiChangeMet, detail: dir === 'call' ? `CE→${matrixLabel(ceOIMatrix, confirmedCeOIMatrix)} / PE→${matrixLabel(peOIMatrix, confirmedPeOIMatrix)}` : `PE→${matrixLabel(peOIMatrix, confirmedPeOIMatrix)} / CE→${matrixLabel(ceOIMatrix, confirmedCeOIMatrix)}` },
    { key: 'vol',     label: 'Volume',     met: volumeMet,
      detail: (() => {
        const cnt  = dir === 'call' ? bullishSpikeCount : dir === 'put' ? bearishSpikeCount : volumeAlerts.length;
        const side = dir === 'call' ? 'bullish' : dir === 'put' ? 'bearish' : 'aligned';
        if (cnt === 0) return hasVolSpike ? `Spikes present — none ${side}` : 'No spikes';
        const qualifier = cnt >= 2 ? ' ✓ TAKE TRADE' : ' — ON RISK only (need 2 for TAKE TRADE)';
        return `${cnt} ${side} spike${cnt > 1 ? 's' : ''}${qualifier}`;
      })() },
    { key: 'iv',      label: 'Vol Expand', met: volExpMet,
      detail: straddleExpansionRatio != null && ivRank != null
        ? `Straddle ${straddleExpansionRatio >= 1 ? '+' : ''}${((straddleExpansionRatio - 1) * 100).toFixed(0)}% · IVR ${ivRank.toFixed(0)}%`
        : straddleExpansionRatio != null
        ? `Straddle ${straddleExpansionRatio >= 1 ? '+' : ''}${((straddleExpansionRatio - 1) * 100).toFixed(0)}% from open`
        : ivRank != null
        ? `IVR ${ivRank.toFixed(0)}% · ${ivRegime ?? 'N/A'}`
        : ivRegime ?? 'N/A' },
  ];

  // allFourMet uses volumeMetFull (≥2 spikes) — TAKE TRADE requires genuine multi-strike conviction.
  // metCount uses the fourFactors array which has volumeMetPartial (≥1 spike) for Factor 3,
  // so a single spike still counts toward the 3/4 threshold that gates ON RISK.
  const allFourMet  = trendMet && oiChangeMet && volumeMetFull && volExpMet;
  const metCount    = fourFactors.filter(f => f.met).length;
  const missingKeys = fourFactors.filter(f => !f.met).map(f => f.label);

  // ── Balanced market detection ──────────────────────────────────────────────
  // Removed third OR (oiSetupSignal === 'Neutral' && !hasVolSpike): full-chain OI neutrality
  // does not invalidate a high-score directional signal. A 7/10 score with a clear direction
  // is a real signal — the third OR was routing it to BALANCED solely because the overall
  // put/call distribution happened to be evenly spread across the chain.
  const isBalanced =
    dir === null ||
    (Math.abs(callScore - putScore) <= 1 && callScore < 4 && putScore < 4);

  const isBuySignal  = signal === 'CALL BUY'  || signal === 'PUT BUY';
  const isBiasSignal = signal === 'CALL BIAS' || signal === 'PUT BIAS';

  // Contra-trend: Trend factor failing because the ATM matrix actively opposes the direction.
  // CE Short Build-up for CALL = call writers adding (bearish for call holders — not just "missing", actively contra).
  // PE Short Build-up for PUT  = put writers adding (bullish for underlying — actively contra PUT).
  // In either case ON RISK entry is suppressed; the system should look for a square-off instead.
  const callTrendContra = dir === 'call' && !trendMet && effectiveCeOIMatrix === 'Short Build-up';
  const putTrendContra  = dir === 'put'  && !trendMet && effectivePeOIMatrix === 'Short Build-up';

  // ON RISK is a genuine, actionable tier traded at 1 whole lot (TAKE TRADE trades 2 whole
  // lots) — both are executable Indian F&O quantities, so there is no fractional-lot problem.
  //   BUY  (≥8/9 checklist) + 3/4 technical factors  — strong checklist, incomplete technicals
  //   BIAS (7/9 checklist)  + all 4 technical factors — moderate checklist, full technical confirmation
  const isOnRisk =
    !isBalanced && !callTrendContra && !putTrendContra && (
      (isBuySignal  && metCount >= 3 && !allFourMet) ||
      (isBiasSignal && allFourMet)
    );

  // ── Trade verdict ──────────────────────────────────────────────────────────
  // TAKE TRADE: all 4 technical factors + BUY (≥8/9 checklist) → 2 lots.
  // ON RISK: 3/4 factors (BUY) or 4/4 factors (BIAS) → 1 lot.
  const verdict =
    isBalanced                          ? 'BALANCED'   :
    signal === 'NO SETUP'               ? 'NO SETUP'   :
    allFourMet && isBuySignal           ? 'TAKE TRADE' :
    isOnRisk                            ? 'ON RISK'    :
    'SKIP';

  const verdictColor =
    verdict === 'TAKE TRADE' && dir === 'call' ? '#00c853' :
    verdict === 'TAKE TRADE' && dir === 'put'  ? '#d32f2f' :
    verdict === 'ON RISK'                       ? '#ff9800' :
    verdict === 'SKIP'                          ? '#d32f2f' :
    '#ff9800';

  const tradeAdviceLabel =
    verdict === 'TAKE TRADE' ? `TAKE ${dir === 'call' ? 'CALL' : 'PUT'} (${takeTradeLots} lot${takeTradeLots === 1 ? '' : 's'})` :
    verdict === 'ON RISK'    ? `TAKE ${dir === 'call' ? 'CALL' : 'PUT'} — ON RISK, ${metCount}/4 factors (${onRiskLots} lot${onRiskLots === 1 ? '' : 's'})` :
    verdict === 'SKIP'       ? 'NO TRADE — wait for stronger confluence' :
    verdict === 'BALANCED'   ? 'NO TRADE — balanced market' :
    'NO TRADE';

  // ── #4: Max Pain proximity ────────────────────────────────────────────────
  const maxPainDiffPct = (maxPainPrice && spotPrice)
    ? ((maxPainPrice - spotPrice) / spotPrice * 100)
    : null;
  // For call buyers: spot below max pain = gravity works FOR us; above = against us
  // For put buyers: spot above max pain = gravity works FOR us; below = against us
  const maxPainWarning = maxPainDiffPct != null && (() => {
    if (dir === 'call' && maxPainDiffPct < -1.0)
      return `Max Pain at ${Math.round(maxPainPrice)} is ${Math.abs(maxPainDiffPct).toFixed(1)}% below spot — market may pin/fall`;
    if (dir === 'put' && maxPainDiffPct > 1.0)
      return `Max Pain at ${Math.round(maxPainPrice)} is ${maxPainDiffPct.toFixed(1)}% above spot — market may pin/rise`;
    return null;
  })();

  // ── #9: Session tracker + circuit breaker (computed early — gates verdict) ──
  // The backend decides: its per-index breaker is off by default (limits.index-breaker) — the day
  // stops on 2 losses in a row across all indices instead (portfolioDailyStop).
  const circuitBreaker = backendSystemStatus
    ? !!backendSystemStatus.circuitBreakerTripped
    : sessionTrades.losses >= 2;
  const sessionTotal   = sessionTrades.wins + sessionTrades.losses;
  const sessionWinRate = sessionTotal > 0
    ? Math.round(sessionTrades.wins / sessionTotal * 100)
    : null;

  // circuitBreaker (above) reflects "2+ losses today" for the rest of this component's
  // display/stats. circuitBreakerGate is what actually blocks new trades: it stays true
  // for a mandatory 15-min cooldown, then can release IF a chain sweep has held for
  // SWEEP_CONFIRM_POLLS consecutive polls in the direction about to be traded — see the
  // episode/persistence tracking in the auto-trade useEffect below. A fresh loss after
  // release re-arms the cooldown from scratch.
  //
  // sessionTrades is synced from the backend SYSTEM engine (see AnalyzerPage.js), so
  // `circuitBreaker` above is backend-accurate; release state and cooldown come straight from
  // the backend too — this panel no longer runs its own circuit-breaker episode logic.
  const effectiveCircuitBreakerReleased = !!backendSystemStatus?.circuitBreakerReleased;
  const circuitBreakerGate       = circuitBreaker && !effectiveCircuitBreakerReleased;
  const cbCooldownRemainingMs    = backendSystemStatus?.cooldownRemainingMs ?? CB_COOLDOWN_MS;
  const cbCooldownOver           = cbCooldownRemainingMs <= 0;

  // ── #5: Support / Resistance proximity ────────────────────────────────────
  // Absolute point thresholds — percentage-based proximity is misleading on
  // high-value indices (e.g. 0.8% on NIFTY = ~195 pts, which is NOT "close").
  const SR_PROXIMITY_PTS = {
    NIFTY: 75, BANKNIFTY: 150, SENSEX: 250, BANKEX: 200,
    MIDCPNIFTY: 50, FINNIFTY: 75, 'NIFTY IT': 60,
  };
  const srThreshold = symbol ? (SR_PROXIMITY_PTS[symbol] ?? spotPrice * 0.003) : spotPrice * 0.003;

  const srWarning = (() => {
    if (!spotPrice) return null;
    if (dir === 'call' && resistance) {
      const pts = parseFloat(resistance) - spotPrice;
      if (pts >= 0 && pts < srThreshold)
        return `Resistance at ${resistance} only ${Math.round(pts)} pts away — writers may defend hard`;
    }
    if (dir === 'put' && support) {
      const pts = spotPrice - parseFloat(support);
      if (pts >= 0 && pts < srThreshold)
        return `Support at ${support} only ${Math.round(pts)} pts away — buyers may absorb`;
    }
    return null;
  })();

  // ── #5: S/R blocking flag ─────────────────────────────────────────────────
  const srBlocking = srWarning != null;
  const srBlocked  = srBlocking && verdict === 'TAKE TRADE';

  // ── #6: PCR divergence detection (enhanced) ──────────────────────────────
  const pcrDivergence = (() => {
    if (pcrHistory.length < 2) return null;
    const len     = pcrHistory.length;
    const current = pcrHistory[len - 1];
    const prev1   = pcrHistory[len - 2];                                // 1-poll ago
    const ref3    = len >= 4 ? pcrHistory[len - 4] : pcrHistory[0];    // 3-poll reference

    const change1 = current - prev1;   // single-poll delta (detects instant spikes)
    const change3 = current - ref3;    // 3-poll trend delta

    // Trigger: single-poll spike ≥ 0.15 OR 3-poll trend ≥ 0.25
    if (Math.abs(change1) < 0.15 && Math.abs(change3) < 0.25) return null;

    // Use whichever magnitude is larger as the primary signal
    const primaryChange = Math.abs(change1) >= Math.abs(change3) ? change1 : change3;
    const fromVal       = Math.abs(change1) >= Math.abs(change3) ? prev1   : ref3;
    const direction     = primaryChange > 0 ? 'PUT_SURGE' : 'CALL_SURGE';
    const pctChange     = fromVal > 0 ? Math.abs(primaryChange / fromVal * 100) : 0;

    // Severity: absolute delta AND percentage move both considered
    const severity =
      Math.abs(primaryChange) >= 0.5 || pctChange >= 50 ? 'SEVERE'   :
      Math.abs(primaryChange) >= 0.3 || pctChange >= 30 ? 'MODERATE' :
      'MILD';

    const severityColor =
      severity === 'SEVERE'   ? '#b71c1c' :
      severity === 'MODERATE' ? '#e65100' : '#f57c00';

    // Absolute PCR level classification
    const levelLabel =
      current < 0.7 ? 'Extreme Call Side' :
      current < 0.9 ? 'Call Bias'         :
      current < 1.1 ? 'Neutral'           :
      current < 1.3 ? 'Put Bias'          : 'Extreme Put Side';

    const isFastSpike = Math.abs(change1) >= 0.15;  // fired in a single poll

    // Does this spike oppose the active trade direction?
    const opposing = dir != null && (
      (direction === 'PUT_SURGE'  && dir === 'call') ||
      (direction === 'CALL_SURGE' && dir === 'put')
    );

    const trend = pcrHistory.slice(-6);  // last 6 values for mini chart

    return {
      direction, severity, severityColor,
      change1, change3, primaryChange, pctChange,
      current, fromVal, levelLabel, isFastSpike, opposing, trend,
    };
  })();

  // SEVERE opposing PCR spike downgrades TAKE TRADE → SKIP. TAKE-TRADE-only (unlike
  // thetaHighBlock/pcrExtremeBlock) — a fast-moving PCR spike opposing a strong 4/4-factor
  // setup is treated as more disqualifying than the same spike against an ON RISK setup.
  const pcrOpposingBlock = pcrDivergence?.opposing && pcrDivergence?.severity === 'SEVERE';

  // Wilson score lower bound (one-sided 90% CI, z = 1.282).
  // Returns the lower bound of the confidence interval for a binomial proportion.
  // Tells us: "even generously, the true WR is at least X% with 90% confidence."
  // Using raw % at n=5 was wrong: at true 50% WR, there's a 50% chance of ≥3 losses
  // in 5 trades → blocked half of valid setups after normal variance.
  const wilsonLower90 = (wins, n) => {
    if (n < 1) return 0;
    const z = 1.282, p = wins / n;
    return (p + z*z/(2*n) - z * Math.sqrt(p*(1-p)/n + z*z/(4*n*n))) / (1 + z*z/n);
  };

  // Adaptive downgrade: TAKE TRADE → SKIP when Wilson lower bound of TT win rate < 40%
  // over ≥15 trades on this symbol. At n=15, ≥9 wins (60%+) is needed to exceed the 40% bound.
  // Minimum of 15 (not 5) because smaller samples have wide confidence intervals that
  // routinely suppress valid setups after a run of normal variance.
  const adaptiveDowngrade = histStats != null
    && histStats.ttCount >= 15
    && histStats.ttWins != null
    && wilsonLower90(histStats.ttWins, histStats.ttCount) < 0.40;

  // Confluence gate: TAKE TRADE requires ≥ 6/10 (MODERATE). confluenceScore draws from sources
  // independent of the 9-condition checklist (VIX direction, PCR trend, EMA regime, gamma flip
  // crossover, lead-stock alignment, BB %B) — this gate prevents a setup that barely clears the
  // checklist from becoming a full TAKE TRADE when the broader confluence is FORMING or WEAK.
  const confluenceGateTT = confluenceScore < 6; // TAKE TRADE needs MODERATE or stronger
  // Detect when confluence (not an earlier gate) is the effective cause of the verdict change.
  // adaptiveDowngrade fires before confluenceGateTT in the chain and owns its own UI section.
  const confluenceDowngradedTT = confluenceGateTT && verdict === 'TAKE TRADE' && !adaptiveDowngrade;

  // IV Rank gate: TAKE TRADE blocked when IVR ≥ 60 — options are in the expensive 40% of their
  // 60-day range. Even a correct directional call can lose money when IV mean-reverts after entry
  // (vol-crush): the option sheds vega value faster than it gains delta value.
  // Null-safe: passes when IVR has not yet been computed (insufficient IV history / first polls).
  const ivRankBlock = ivRank != null && ivRank >= 60 && verdict === 'TAKE TRADE';

  // Theta carry-cost gate — mirrors TradingSignalService backend logic exactly.
  // directionalThetaPct: daily theta as % of ATM premium for the active trade side. The gate
  // judges it over an intraday hold (riskplan.theta-hold-minutes, default 60 of the 375-minute
  // session), not a whole day — a per-day cap rejected every near weekly.
  // > 2.5% over the hold → TAKE TRADE → ON RISK.  > 4.0% → blocks TAKE TRADE and ON RISK.
  // thetaModerateBlock only demotes TAKE TRADE → ON RISK; it must stay TAKE-TRADE-only, since
  // a verdict that's already ON RISK (base 3/4-factor path) has nothing further to demote to.
  const directionalThetaPct = dir === 'put' ? thetaPutPct : thetaCallPct;
  const holdThetaPct = directionalThetaPct != null ? directionalThetaPct * THETA_HOLD_MINUTES / 375 : null;
  const thetaHighBlock    = holdThetaPct != null && holdThetaPct > 4.0
                              && (verdict === 'TAKE TRADE' || verdict === 'ON RISK');
  const thetaModerateBlock = holdThetaPct != null && holdThetaPct > 2.5
                              && verdict === 'TAKE TRADE';

  // Time block — adaptive AVOID_OPEN window:
  //   Gap open (|gapOpenPct| ≥ 0.5%):  9:15–9:45 (full 30 min) — gap traps, elevated IV
  //   Flat open (< 0.5%):              9:15–9:30 (15 min only)  — volatility settles faster
  //   gapOpenPct null (first polls):   9:15–9:45 (conservative default until data arrives)
  // Uses BOTH the real IST clock AND the backend timeZone field.
  const isGapDay         = gapOpenPct == null || Math.abs(gapOpenPct) >= 0.5;
  const openAvoidEndMins = isGapDay ? 9 * 60 + 45 : 9 * 60 + 30;
  const openAvoidLabel   = isGapDay ? '9:45' : '9:30';
  const { clockAvoidOpen, clockAvoidClose } = (() => {
    const ist     = new Date(new Date().toLocaleString('en-US', { timeZone: 'Asia/Kolkata' }));
    const istMins = ist.getHours() * 60 + ist.getMinutes();
    return {
      clockAvoidOpen:  istMins >= 9 * 60 + 15 && istMins < openAvoidEndMins,
      clockAvoidClose: istMins >= 15 * 60 + 10  && istMins < 15 * 60 + 40,
    };
  })();
  const timeBlock = clockAvoidOpen || clockAvoidClose || timeZone === 'AVOID_OPEN' || timeZone === 'AVOID_CLOSE';

  // ── Combined verdict gate ──────────────────────────────────────────────────
  // Priority order (highest → lowest), matching the finalVerdict ternary chain below exactly.
  // Two independent paths can produce ON RISK (1 lot): (a) the base `verdict` itself, when
  // 3/4 factors + BUY or 4/4 factors + BIAS, falls through the cascade untouched; (b)
  // thetaModerateBlock demoting a would-be TAKE TRADE (2 lots) down to ON RISK due to
  // elevated theta carry cost. Every other gate resolves to SKIP — both TAKE TRADE and ON
  // RISK are whole-lot, executable sizes, so "no trade" is the only remaining non-SKIP outcome.
  //   Circuit Break           → CIRCUIT BREAK  (no exceptions — a chain sweep used to bypass
  //                                             this, which let the breaker reopen new trades
  //                                             on days it should have stopped trading entirely)
  //   Daily Limit             → DAILY LIMIT  (backend portfolio daily stop or this index's lock)
  //   Profit Locked           → PROFIT LOCKED (backend profit lock: the day's goal is banked)
  //   Time Block              → SKIP     (9:15–9:30 flat / 9:15–9:45 gap-day open / 15:10–15:40 close)
  //   S/R Block               → SKIP     (spot within threshold of key level)
  //   Sideways Block          → SKIP     (TAKE TRADE only, 2+ TFs sideways — theta erodes buyers)
  //   PCR opposing            → SKIP     (TAKE TRADE only, SEVERE opposing spike)
  //   Lead stock opposing     → SKIP     (STRONG/MODERATE heavyweights against direction)
  //   Adaptive downgrade      → SKIP     (TAKE TRADE only, TT WR < 40% over ≥5 trades)
  //   IV Rank block           → SKIP     (TAKE TRADE only, IVR ≥ 60% — expensive premium)
  //   Theta high block        → SKIP     (TAKE TRADE or ON RISK, theta > 4%/day of premium)
  //   Theta moderate block    → ON RISK  (TAKE TRADE only, theta > 2.5%/day — demotes to 1 lot)
  //   Confluence gate         → SKIP     (TAKE TRADE only, confluenceScore < 6)
  //   PCR extreme block       → SKIP     (TAKE TRADE or ON RISK, PCR crowd extreme <0.6 or >1.4)
  //   Base verdict            (TAKE TRADE=2 lots / ON RISK=1 lot / SKIP / BALANCED / NO SETUP)
  const leadStockBlock =
    (dir === 'call' && leadStockOpposingCall && verdict === 'TAKE TRADE') ||
    (dir === 'put'  && leadStockOpposingPut  && verdict === 'TAKE TRADE');

  // PCR extreme reversal guard: crowd maximally positioned in the same direction as the trade.
  // "Over-Bearish" (PCR<0.6) = call-heavy crowd — entering CALL BUY joins the maximum crowd.
  // "Over-Bullish" (PCR>1.4) = put-heavy crowd  — entering PUT BUY joins the maximum crowd.
  // Both are high-reversal-risk environments regardless of position size — block both
  // TAKE TRADE and ON RISK entries, same reasoning as thetaHighBlock above.
  const pcrExtremeBlock =
    (totalOI === 'Over-Bearish' && dir === 'call' && (verdict === 'TAKE TRADE' || verdict === 'ON RISK')) ||
    (totalOI === 'Over-Bullish' && dir === 'put'  && (verdict === 'TAKE TRADE' || verdict === 'ON RISK'));

  // Premium/OI conflict — mirrors the backend's premium-OI alignment gate: TAKE TRADE / ON RISK
  // only when the premium signal (CALL BUY / PUT BUY) agrees with the trade direction. The
  // backend SKIPs otherwise (no Telegram alert, no SYSTEM entry), so the cockpit must too.
  const premiumRec = gammaData?.premiumRecommendation;
  const premiumConflictBlock = (verdict === 'TAKE TRADE' || verdict === 'ON RISK')
    && premiumRec !== (dir === 'call' ? 'CALL BUY' : dir === 'put' ? 'PUT BUY' : '__none__');

  // Backend daily limits — the portfolio daily stop (losses in a row / −R / trade cap) or this
  // index's lock. The backend overrides every entry with DAILY LIMIT while either holds, so the
  // panel must too, or it shows TAKE CALL while the engine can't trade.
  const dailyLimitReason = backendSystemStatus?.portfolioDailyStop || backendSystemStatus?.indexLock || null;
  // Profit lock — the day reached its goal (% of capital) and then gave some back (or STOP mode).
  const profitLockReason = !backendSystemStatus?.portfolioDailyStop ? (backendSystemStatus?.profitLock || null) : null;
  // Kite WS down/stalled — the backend pauses every new entry until 5 s ticks are back.
  const feedFallbackReason = backendSystemStatus?.feedFallback || null;

  const finalVerdict =
    circuitBreakerGate                                   ? 'CIRCUIT BREAK' :
    backendSystemStatus?.portfolioDailyStop              ? 'DAILY LIMIT'   :
    profitLockReason                                     ? 'PROFIT LOCKED' :
    dailyLimitReason                                     ? 'DAILY LIMIT'   :
    feedFallbackReason                                   ? 'FEED FALLBACK' :
    timeBlock                                            ? 'SKIP'          :
    srBlocked                                            ? 'SKIP'          :
    premiumConflictBlock                                 ? 'SKIP'          :
    sidewaysBlock && verdict === 'TAKE TRADE'            ? 'SKIP'          :
    pcrOpposingBlock && verdict === 'TAKE TRADE'         ? 'SKIP'          :
    leadStockBlock                                       ? 'SKIP'          :
    adaptiveDowngrade && verdict === 'TAKE TRADE'        ? 'SKIP'          :
    ivRankBlock                                          ? 'SKIP'          :
    thetaHighBlock                                       ? 'SKIP'          :
    thetaModerateBlock                                   ? 'ON RISK'       :
    confluenceGateTT  && verdict === 'TAKE TRADE'        ? 'SKIP'          :
    pcrExtremeBlock                                      ? 'SKIP'          :
    verdict;

  const finalVerdictColor =
    finalVerdict === 'CIRCUIT BREAK'                    ? '#5d0000'       :
    finalVerdict === 'DAILY LIMIT'                      ? '#5d0000'       :
    finalVerdict === 'PROFIT LOCKED'                    ? '#1b5e20'       :
    finalVerdict === 'FEED FALLBACK'                    ? '#5d0000'       :
    finalVerdict === 'TAKE TRADE' && dir === 'call'     ? '#00c853'       :
    finalVerdict === 'TAKE TRADE' && dir === 'put'      ? '#d32f2f'       :
    finalVerdict === 'ON RISK'                          ? '#ff9800'       :
    finalVerdict === 'SKIP'                             ? '#d32f2f'       :
    '#ff9800';

  const finalTradeAdviceLabel =
    finalVerdict === 'CIRCUIT BREAK'   ? `STOP TRADING — ${sessionTrades.losses} consecutive losses, circuit breaker active` :
    finalVerdict === 'DAILY LIMIT'     ? `NO NEW TRADES TODAY — daily limit: ${dailyLimitReason}` :
    finalVerdict === 'PROFIT LOCKED'   ? `NO NEW TRADES TODAY — profit locked: ${profitLockReason}` :
    finalVerdict === 'FEED FALLBACK'   ? `NO NEW TRADES — live feed fallback: ${feedFallbackReason}` :
    timeBlock && (clockAvoidOpen  || timeZone === 'AVOID_OPEN')  ? `NO TRADE — Market open zone (9:15–${openAvoidLabel}), wait for ${openAvoidLabel} candle confirmation${isGapDay && gapOpenPct != null ? ` (gap ${gapOpenPct > 0 ? '+' : ''}${gapOpenPct.toFixed(2)}%)` : ''}` :
    timeBlock && (clockAvoidClose || timeZone === 'AVOID_CLOSE') ? 'NO TRADE — End-of-day zone (15:10–15:40), theta accelerating — no new entries' :
    srBlocked && dir === 'call'        ? `NO TRADE — Resistance at ${resistance} too close, wait for confirmed breakout` :
    srBlocked && dir === 'put'         ? `NO TRADE — Support at ${support} too close, wait for confirmed breakdown`     :
    premiumConflictBlock               ? `NO TRADE — Premium signal (${premiumRec || 'NO TRADE'}) doesn't confirm the ${dir?.toUpperCase()} OI signal` :
    // sidewaysBlock/pcrOpposingBlock/adaptiveDowngrade are bare conditions (unlike
    // leadStockBlock/ivRankBlock/thetaHighBlock/pcrExtremeBlock, which bake the verdict
    // check into their own definitions) — must re-check verdict==='TAKE TRADE' here too,
    // matching the cascade exactly, or an ON RISK verdict could show contradictory "NO
    // TRADE" hero text while finalVerdict is genuinely ON RISK (1 lot).
    sidewaysBlock && verdict === 'TAKE TRADE'    ? 'NO TRADE — Sideways market, theta decay works against option buyers' :
    pcrOpposingBlock && verdict === 'TAKE TRADE' ? `NO TRADE — ${pcrDivergence.direction === 'PUT_SURGE' ? 'PUT surge' : 'CALL surge'} opposes ${dir?.toUpperCase()} signal` :
    leadStockBlock                     ? `NO TRADE — Lead stocks ${leadingStocksData?.compositeBias} (${leadingStocksData?.leadStrength}) oppose ${dir?.toUpperCase()}` :
    adaptiveDowngrade && verdict === 'TAKE TRADE' ? `NO TRADE — Statistical WR too low on ${symbol}: ${histStats?.ttWins ?? 0}W/${histStats?.ttCount ?? 0} TT (lower bound ${(wilsonLower90(histStats?.ttWins ?? 0, histStats?.ttCount ?? 1) * 100).toFixed(0)}% < 40%)` :
    ivRankBlock                        ? `NO TRADE — IV Rank ${ivRank?.toFixed(0)}% (≥60%) — premium expensive vs 60-day history, vol-crush risk` :
    thetaHighBlock                     ? `NO TRADE — Theta ${holdThetaPct?.toFixed(1)}% of premium over a ${THETA_HOLD_MINUTES}-min hold (>4%): decay faster than a realistic intraday move` :
    thetaModerateBlock                 ? `TAKE ${dir === 'call' ? 'CALL' : 'PUT'} — ON RISK, Theta ${holdThetaPct?.toFixed(1)}% of premium over a ${THETA_HOLD_MINUTES}-min hold (>2.5%): reduce to ${onRiskLots} lot${onRiskLots === 1 ? '' : 's'} to manage carry cost` :
    pcrExtremeBlock && dir === 'call'  ? `NO TRADE — PCR extreme (${totalOI}): crowd maximally long calls, reversal risk` :
    pcrExtremeBlock && dir === 'put'   ? `NO TRADE — PCR extreme (${totalOI}): crowd maximally long puts, reversal risk` :
    confluenceDowngradedTT             ? `NO TRADE — Weak confluence (${confluenceScore}/10, need ≥6)` :
    finalVerdict === 'TAKE TRADE'      ? `TAKE ${dir === 'call' ? 'CALL' : 'PUT'} (${takeTradeLots} lot${takeTradeLots === 1 ? '' : 's'})` :
    // Base-verdict ON RISK (3/4 factors + BUY, or 4/4 + BIAS) — no named gate demoted it,
    // it's simply the verdict itself. Reuse tradeAdviceLabel so the metCount detail survives.
    finalVerdict === 'ON RISK'         ? tradeAdviceLabel :
    // No named gate fired — finalVerdict fell through to the base verdict itself.
    finalVerdict === 'SKIP'            ? tradeAdviceLabel :
    finalVerdict === 'BALANCED'        ? 'NO TRADE — balanced market' :
    'NO TRADE';

  // Per-stock lead detail for blocking reason
  const leadStockBlockReason = (() => {
    if (!leadStockBlock || !leadingStocksData?.stocks) return null;
    const opposing = leadingStocksData.stocks.filter(s =>
      (dir === 'call' && s.direction === 'BEARISH') ||
      (dir === 'put'  && s.direction === 'BULLISH')
    );
    const lines = opposing.map(s =>
      `${s.symbol} ${s.direction} (price ${s.priceChangePct >= 0 ? '+' : ''}${s.priceChangePct?.toFixed(2) ?? '?'}%` +
      `${s.futuresPremium ? `, fut ${s.futuresPremium >= 0 ? '+' : ''}${s.futuresPremium.toFixed(1)}` : ''})`
    );
    return `LEAD STOCKS ${leadingStocksData.leadStrength} OPPOSING — ${lines.join(' · ')} — ` +
           `heavyweights lead index by 1–3 min, skip this trade`;
  })();

  // Blocking reasons for the NO TRADE list — ordered to EXACTLY mirror the finalVerdict
  // cascade below (circuitBreakerGate excluded here since it has its own dedicated banner).
  // This ensures the reason that actually determined finalVerdict always sorts first, so
  // the default top-3 view (see allNoTradeReasons.slice(0,3) at render) never hides the
  // real cause behind lower-priority informational reasons that happen to also be true.
  const allNoTradeReasons = [
    ...(profitLockReason && !circuitBreakerGate ? [
      `PROFIT LOCKED — ${profitLockReason}. No new SYSTEM entries for the rest of today; an open position is still managed.`,
    ] : []),
    ...(dailyLimitReason && !profitLockReason && !circuitBreakerGate ? [
      `DAILY LIMIT — ${dailyLimitReason}. No new SYSTEM entries for the rest of today; an open position is still managed.`,
    ] : []),
    ...(backendSystemStatus?.dayType === 'CHOP' && !dailyLimitReason && !profitLockReason ? [
      `CHOP DAY — ${backendSystemStatus.dayTypeNote}. ${backendSystemStatus.dayTypeEnforced ? 'No new SYSTEM buys while it holds.' : 'Shadow: logged only.'}`,
    ] : []),
    ...(backendSystemStatus?.dayType === 'UNSURE' && backendSystemStatus?.dayTypeEnforced && !dailyLimitReason && !profitLockReason ? [
      `UNSURE DAY — ${backendSystemStatus.dayTypeNote}. New SYSTEM buys at reduced size.`,
    ] : []),
    ...(backendSystemStatus?.profitLockHalfSize && !profitLockReason && !dailyLimitReason ? [
      `DAY GOAL REACHED — ${backendSystemStatus.dayNetPctOfCapital >= 0 ? '+' : ''}${backendSystemStatus.dayNetPctOfCapital}% of capital today. New SYSTEM entries are half size.`,
    ] : []),
    ...(feedFallbackReason && !dailyLimitReason && !circuitBreakerGate ? [
      `FEED FALLBACK — ${feedFallbackReason}. No new SYSTEM entries until Kite ticks are back; an open position is still managed.`,
    ] : []),
    ...(timeBlock ? [
      (clockAvoidOpen || timeZone === 'AVOID_OPEN')
        ? `TIME BLOCK — Market open (9:15–${openAvoidLabel}): price discovery chaotic, spreads wide. No new entries until ${openAvoidLabel}${isGapDay && gapOpenPct != null ? ` (gap day: ${gapOpenPct > 0 ? '+' : ''}${gapOpenPct.toFixed(2)}% — full 30-min block)` : !isGapDay ? ' (flat open — 15-min block)' : ''}`
        : 'TIME BLOCK — End-of-day (15:10–15:40): theta burning fast, unwinding pressure. No new entries',
    ] : []),
    ...(srBlocked        ? [`S/R BLOCK — ${srWarning}`] : []),
    // sidewaysBlock/pcrOpposingBlock/adaptiveDowngrade only actually gate TAKE TRADE (see
    // finalVerdict cascade) — re-check verdict==='TAKE TRADE' here too, or these "No new
    // entries" reasons could list under an ON RISK header while a 1-lot entry is genuinely
    // happening, the same contradiction fixed for finalTradeAdviceLabel above.
    ...(sidewaysBlock && verdict === 'TAKE TRADE'
      ? ['SIDEWAYS MARKET — 2+ timeframes flat/conflicting. Theta will erode option premium. No new entries until trend establishes.'] : []),
    ...(pcrOpposingBlock && verdict === 'TAKE TRADE' ? [
      `PCR ${pcrDivergence.severity} ALERT — PCR ${pcrDivergence.fromVal.toFixed(2)} → ${pcrDivergence.current.toFixed(2)} ` +
      `(${pcrDivergence.primaryChange > 0 ? '+' : ''}${pcrDivergence.primaryChange.toFixed(2)}, ${pcrDivergence.pctChange.toFixed(0)}% change) — ` +
      `opposing ${dir?.toUpperCase()} signal`,
    ] : []),
    ...(leadStockBlockReason ? [leadStockBlockReason] : []),
    ...(adaptiveDowngrade && verdict === 'TAKE TRADE' ? [
      `ADAPTIVE GATE — ${histStats?.ttWins ?? 0}W / ${histStats?.ttCount ?? 0} TAKE TRADE trades on ${symbol}. ` +
      `Wilson 90% confidence lower bound: ${(wilsonLower90(histStats?.ttWins ?? 0, histStats?.ttCount ?? 1) * 100).toFixed(0)}% ` +
      `(raw ${histStats?.ttWR ?? '?'}%) — statistically below 40% threshold even with generous uncertainty. ` +
      `Requires ≥15 trades before activating to avoid blocking valid setups after normal variance.`,
    ] : []),
    ...(ivRankBlock ? [
      `IV RANK GATE — IVR ${ivRank?.toFixed(0)}% (≥60%): options are in the expensive 40% of their ` +
      `60-day IV range. TAKE TRADE skipped — vol-crush can erase directional gains ` +
      `when IV mean-reverts after your entry.`,
    ] : []),
    ...(thetaHighBlock ? [
      `THETA GATE (SKIP) — Theta ${directionalThetaPct?.toFixed(1)}%/day > 4% of premium. ` +
      `Daily time decay is structurally faster than realistic intraday option moves — ` +
      `a correct directional call still loses value. No size makes this trade viable for a buyer. ` +
      `Wait for a lower-theta environment (earlier DTE, lower IV, or after a premium correction).`,
    ] : []),
    ...(thetaModerateBlock ? [
      `THETA GATE (ON RISK) — Theta ${directionalThetaPct?.toFixed(1)}%/day > 2.5% of premium. ` +
      `Downgraded from TAKE TRADE: carry cost is elevated — buyer needs 2.5%+ intraday option move ` +
      `just to break even on theta. If entering: 1 lot only, tighter stop, target 3:1 minimum.`,
    ] : []),
    ...(confluenceDowngradedTT ? [
      `CONFLUENCE GATE — Score ${confluenceScore}/10 (need ≥6 for TAKE TRADE). Skipped — ` +
      `more independent signals need to agree before entry.`,
    ] : []),
    ...(pcrExtremeBlock ? [
      `PCR EXTREME BLOCK — ${totalOI} (${dir === 'call' ? 'PCR < 0.6: call-heavy crowd' : 'PCR > 1.4: put-heavy crowd'}). ` +
      `Entering ${dir?.toUpperCase()} BUY here joins the maximum crowd position — high contrarian reversal risk. ` +
      `Wait for PCR to normalise (0.6–1.4 range) before re-entry.`,
    ] : []),
    // Informational-only, raw-signal cautions (confluenceEngine) — never determine
    // finalVerdict, so they always rank last regardless of how many are true.
    ...noTradeReasons,
  ];

  // ── #2: Expiry selection guidance (rich) ─────────────────────────────────
  const expiryGuide = (() => {
    if (daysToExpiry == null) return null;

    // Which side are we analysing?
    const sideTheta = dir === 'put'
      ? (atmPutTheta  != null ? Math.abs(atmPutTheta)  : null)
      : (atmCallTheta != null ? Math.abs(atmCallTheta) : null);
    const sideLTP   = dir === 'put' ? atmPutLTP   : atmCallLTP;
    const sideThetaPct = dir === 'put' ? thetaPutPct : thetaCallPct;

    // ₹ decay per day & total burden
    const thetaPerDay       = sideTheta  != null ? sideTheta  : null;
    const totalThetaBurden  = (thetaPerDay != null && daysToExpiry > 0)
      ? thetaPerDay * daysToExpiry : null;

    // DTE category
    const dte = daysToExpiry;
    let type, color, verdict, rec;
    if (dte === 0) {
      type = '0DTE'; color = '#b71c1c';
      verdict = 'EXTREME RISK';
      rec = 'Same-day expiry. Gamma is exponential, theta is maximum. Intraday scalp only — exit before 14:30.';
    } else if (dte <= 2) {
      type = 'WEEKLY'; color = '#c62828';
      verdict = 'HIGH THETA';
      rec = 'You need the move TODAY or tomorrow. Theta decays premium fast — any delay kills your trade.';
    } else if (dte <= 7) {
      type = 'WEEKLY'; color = '#2e7d32';
      verdict = 'IDEAL (INTRADAY)';
      rec = 'Nearest weekly: most delta and gamma per rupee, tightest spreads. Theta over an intraday hold is small — buy ATM or 1 ITM (Δ 0.50–0.60).';
    } else if (dte <= 14) {
      type = 'NEAR-MONTHLY'; color = '#f9a825';
      verdict = 'ACCEPTABLE';
      rec = 'More vega, less gamma than the weekly. For intraday, stay ATM or 1 ITM (Δ 0.50–0.60) — never OTM.';
    } else {
      type = 'MONTHLY'; color = '#e65100';
      verdict = 'VEGA-HEAVY';
      rec = 'Not for intraday scalps: an IV dip can erase a correct move, and wider spreads eat it. Use ATM or 1 ITM only (e.g. BANKNIFTY, which has no weeklies).';
    }

    // Intraday carry: theta over the hold the backend's theta gate judges (THETA_HOLD_MINUTES).
    const holdCarryPct = sideThetaPct != null ? sideThetaPct * THETA_HOLD_MINUTES / 375 : null;

    return {
      dte, type, color, verdict, rec,
      thetaPerDay, totalThetaBurden, sideThetaPct, sideLTP, holdCarryPct,
    };
  })();

  // ── #1: Strike selection — show ITM / ATM / OTM options with recommendation
  const strikeOptions = (() => {
    if (!strikeData.length || !spotPrice || !dir) return null;
    const dte = daysToExpiry ?? 7;

    if (dir === 'call') {
      // ATM: closest strike to spot
      const atm = strikeData
        .filter(s => s.callDelta != null)
        .sort((a, b) => Math.abs(a.strikePrice - spotPrice) - Math.abs(b.strikePrice - spotPrice))[0] || null;
      // ITM: one strike below ATM
      const itm = atm ? strikeData
        .filter(s => s.strikePrice < atm.strikePrice && s.callDelta != null)
        .sort((a, b) => b.strikePrice - a.strikePrice)[0] || null : null;
      // OTM: lowest strike above spot with delta between 0.25 and 0.48
      const otm = strikeData
        .filter(s => s.strikePrice > spotPrice && s.callDelta != null && s.callDelta >= 0.25 && s.callDelta < 0.48)
        .sort((a, b) => a.strikePrice - b.strikePrice)[0] || null;
      return { itm, atm, otm, recommended: recommendStrike(atm, itm, 'callDelta'), side: 'CE', dte };
    }

    if (dir === 'put') {
      // ATM: closest strike to spot
      const atm = strikeData
        .filter(s => s.putDelta != null)
        .sort((a, b) => Math.abs(a.strikePrice - spotPrice) - Math.abs(b.strikePrice - spotPrice))[0] || null;
      // ITM: one strike above ATM
      const itm = atm ? strikeData
        .filter(s => s.strikePrice > atm.strikePrice && s.putDelta != null)
        .sort((a, b) => a.strikePrice - b.strikePrice)[0] || null : null;
      // OTM: highest strike below spot with |putDelta| between 0.25 and 0.48
      const otm = strikeData
        .filter(s => s.strikePrice < spotPrice && s.putDelta != null && Math.abs(s.putDelta) >= 0.25 && Math.abs(s.putDelta) < 0.48)
        .sort((a, b) => b.strikePrice - a.strikePrice)[0] || null;
      return { itm, atm, otm, recommended: recommendStrike(atm, itm, 'putDelta'), side: 'PE', dte };
    }
    return null;
  })();

  // ── #3: Multi-timeframe confirmation ──────────────────────────────────────
  const htfRegime     = emaData?.higherTfRegime;
  const dailyBias     = emaData?.dailyBias;
  const htfConflict   = htfRegime === 'CONFLICTING';
  const htfMatchesDir = (dir === 'call' && htfRegime === 'ALIGNED_BULL') ||
                        (dir === 'put'  && htfRegime === 'ALIGNED_BEAR');
  const dailyMatchesDir = (dir === 'call' && dailyBias === 'BULLISH') ||
                          (dir === 'put'  && dailyBias === 'BEARISH');
  // Full alignment: 5-min + 15-min + daily all agree with signal direction
  const fullAlignment = htfMatchesDir && dailyMatchesDir;
  const dailyConflict = dir != null && dailyBias != null && !dailyMatchesDir;

  // Chain-sweep Telegram alerts are now sent autonomously by TradingSignalService on the
  // backend — the client-side transition-detector + onChainSweepAlert sender were removed
  // to avoid duplicate messages when a tab happens to be open. The on-screen chain-sweep
  // banner below (ts-chain-sweep) still reads chainSweepBull/chainSweepBear directly.

  // ── Verdict lift-up + backend position mirror ────────────────────────────
  // Deps include spotPrice so this re-syncs on EVERY poll, not only on verdict transitions.
  useEffect(() => {   // eslint-disable-line react-hooks/exhaustive-deps
    if (!shouldRender) {
      onVerdictChange?.({ finalVerdict: null, dir: null });
      return;
    }

    // Lift the live verdict up so a manual trade started elsewhere (Greek Stop-Loss panel)
    // can tag itself with the same TAKE TRADE / ON RISK verdict this panel is showing.
    onVerdictChange?.({ finalVerdict, dir });

    // ── Mirror the backend SYSTEM engine (every user) ───────────────────────
    // The backend's TradingSignalService opens, manages and closes positions for this symbol
    // autonomously — liquidity-checked entries, underlying-stop / 2R-target / time-stop exits,
    // 15:15 square-off — whether or not any browser tab is open. This panel used to run its own
    // independent copy of the entry/exit/circuit-breaker lifecycle for non-admin users, which
    // drifted from the backend (old "Signal Ended" exits, LTP pricing, 0.0 counted as a win)
    // and stopped managing a position the moment the tab closed. Now every user sees the
    // backend's real open position here (read-only); SYSTEM trades already appear in each
    // user's Trade History via refreshTradeHistory in AnalyzerPage.
    const backendOpen = backendSystemStatus?.openPosition;
    autoTradeRef.current = backendOpen ? { ...backendOpen } : null;
    setIsAutoTracking(!!backendOpen);
  }, [finalVerdict, dir, shouldRender, spotPrice, onVerdictChange, backendSystemStatus]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!shouldRender) return null;

  return (
    <div className="ts-panel">

      {/* ── Circuit Breaker (top of panel) ──────────────────────────────────
          Three states while circuitBreaker is true:
            1. Mandatory cooldown (< 15 min since trip)      → full stop, countdown shown
            2. Cooldown over, no confirmed sweep yet         → still stopped, waiting for one
            3. Released (confirmed sweep held 3+ polls)      → trading resumed, amber caution banner */}
      {circuitBreaker && !effectiveCircuitBreakerReleased && (
        <div className="ts-cb-panel">
          <div className="ts-cb-heading">STOP TRADING TODAY</div>

          <div className="ts-cb-stats">
            <span className="ts-cb-stat-badge ts-cb-loss">{sessionTrades.losses} losses</span>
            {sessionTrades.wins > 0 && (
              <span className="ts-cb-stat-badge ts-cb-win">{sessionTrades.wins} wins</span>
            )}
            {sessionWinRate != null && (
              <span className="ts-cb-stat-badge ts-cb-rate">{sessionWinRate}% win rate today</span>
            )}
          </div>

          <div className="ts-cb-rule">
            {cbCooldownOver
              ? `2-loss circuit breaker triggered. Mandatory 15-min cooldown is over — trading stays locked until a chain sweep holds for ${SWEEP_CONFIRM_POLLS}+ consecutive polls in one direction (not a single noisy reading).`
              : `2-loss circuit breaker triggered. Locked for a mandatory 15-min cooldown — ${Math.ceil(cbCooldownRemainingMs / 60000)} min remaining. Can only resume after that if a chain sweep holds for ${SWEEP_CONFIRM_POLLS}+ consecutive polls.`
            }
          </div>

          <div className="ts-cb-why">
            <div className="ts-cb-why-title">Why revenge trading destroys accounts</div>
            <div className="ts-cb-why-item">
              <span className="ts-cb-why-n">1</span>
              After loss #1 your judgment is impaired — you start taking setups you normally skip
            </div>
            <div className="ts-cb-why-item">
              <span className="ts-cb-why-n">2</span>
              After loss #2 it becomes emotional — the goal shifts from "good trade" to "win the money back"
            </div>
            <div className="ts-cb-why-item">
              <span className="ts-cb-why-n">3</span>
              Every trade after that increases size, lowers standards, and compounds losses — this is how accounts blow up
            </div>
          </div>

          <div className="ts-cb-action">
            Step away from the screen during the cooldown. Only a persistently-confirmed institutional
            sweep signal — not a single poll — will reopen trading after that.
          </div>
        </div>
      )}

      {circuitBreaker && effectiveCircuitBreakerReleased && (
        <div className="ts-cb-panel ts-cb-panel-exception">
          <div className="ts-cb-heading">⚡ CIRCUIT BREAKER RELEASED — CONFIRMED CHAIN SWEEP</div>
          <div className="ts-cb-stats">
            <span className="ts-cb-stat-badge ts-cb-loss">{sessionTrades.losses} losses today</span>
          </div>
          <div className="ts-cb-rule">
            15-min cooldown passed and a chain sweep held for {SWEEP_CONFIRM_POLLS}+ consecutive polls in the
            {dir === 'call' ? ' bullish' : dir === 'put' ? ' bearish' : ''} direction — trading has resumed.
            A fresh loss will re-arm the breaker and require this to happen again.
          </div>
          <div className="ts-cb-action">
            Trade normally, but stay disciplined — this was an exception, not a clean slate.
          </div>
        </div>
      )}

      {/* ── Header ───────────────────────────────────────────────────────── */}
      <div className="ts-header">
        <span className="ts-title">Trading Setup</span>
        <div className="ts-header-right">
          {sessionTotal > 0 && (
            <span
              className="ts-header-session"
              style={{
                color: circuitBreaker      ? '#b71c1c' :
                       sessionWinRate >= 50 ? '#2e7d32' : '#c62828',
              }}
              title="SYSTEM engine trades today (drives the circuit breaker)"
            >
              SYS&nbsp;{sessionTrades.wins}W&nbsp;/&nbsp;{sessionTrades.losses}L
              {sessionWinRate != null && <>&nbsp;·&nbsp;{sessionWinRate}%</>}
            </span>
          )}
          {manualToday.wins + manualToday.losses > 0 && (
            <span
              className="ts-header-session"
              style={{ color: manualToday.wins >= manualToday.losses ? '#2e7d32' : '#c62828' }}
              title="Your manual trades today (not counted by the circuit breaker)"
            >
              MAN&nbsp;{manualToday.wins}W&nbsp;/&nbsp;{manualToday.losses}L
            </span>
          )}
          <span className="ts-signal" style={{ background: signalColor }}>{signal}</span>
        </div>
      </div>

      {/* ── Verdict Hero — pinned just below header, first thing visible ─── */}
      <div className="ts-verdict-hero" style={{ background: finalVerdictColor }}>
        <span className="ts-verdict-hero-text">{finalTradeAdviceLabel}</span>
        {(spotPrice != null || (dir && atmStrike != null)) && (
          <div className="ts-verdict-hero-sub">
            {spotPrice != null && (
              <span className="ts-verdict-hero-spot">
                Spot {Math.round(spotPrice).toLocaleString('en-IN')}
              </span>
            )}
            {dir && atmStrike != null && (() => {
              const ltp = dir === 'call' ? atmCallLTP : atmPutLTP;
              return (
                <span className="ts-verdict-hero-ltp">
                  {dir === 'call' ? 'CE' : 'PE'} {atmStrike}{ltp != null ? ` ₹${ltp.toFixed(1)}` : ''}
                </span>
              );
            })()}
            {confluenceScore > 0 && (
              <span className="ts-verdict-hero-score">Conf {confluenceScore}/10</span>
            )}
          </div>
        )}
      </div>

      {/* ── Backend verdict strip — display-only, computed autonomously by
          TradingSignalService regardless of whether this tab is open. Does not
          feed into finalVerdict or any auto-tracking logic here (Phase 1). ── */}
      {backendVerdict && backendVerdict.symbol === symbol && (
        <div className="ts-trend-row" title="Computed by the backend engine — trades even when no browser tab is open">
          <span className="ts-market-label">Backend Verdict</span>
          <span className="ts-market-value">
            {backendVerdict.finalVerdict}
            {backendVerdict.dir ? ` (${backendVerdict.dir.toUpperCase()})` : ''}
            {backendVerdict.numLots ? ` · ${backendVerdict.numLots} lot${backendVerdict.numLots > 1 ? 's' : ''}` : ''}
            {backendVerdict.confluenceScore != null ? ` · Conf ${backendVerdict.confluenceScore}/10` : ''}
          </span>
        </div>
      )}
      {/* Entry timing: what an armed setup waits for (pullback to VWAP / breakout retest), or
          why it entered. The cockpit verdict above doesn't apply this gate — the backend does. */}
      {backendVerdict && backendVerdict.symbol === symbol && backendVerdict.dayTypeNote && (
        <div className="ts-trend-row" title="Backend range-day detector — a range day blocks new entries until the day's range breaks">
          <span className="ts-market-label">Day Type</span>
          <span className="ts-market-value">{backendVerdict.dayTypeNote}</span>
        </div>
      )}
      {backendVerdict && backendVerdict.symbol === symbol && backendVerdict.entryNote && (
        <div className="ts-trend-row">
          <span className="ts-market-label">Entry Timing</span>
          <span className="ts-market-value">{backendVerdict.entryNote}</span>
        </div>
      )}

      <div className="ts-trend-row">
        <span className="ts-market-label">Market Trend</span>
        <span className="ts-market-value">{marketTrend}</span>
      </div>

      {vixDirection && (
        <div className="ts-trend-row">
          <span className="ts-market-label">India VIX</span>
          <span className="ts-market-value" style={{
            color: vixDirection === 'declining' ? '#00c853' :
                   vixDirection === 'rising'    ? '#d32f2f' : '#9e9e9e'
          }}>
            {vixDirection === 'declining' ? '📉 Declining — Bullish' :
             vixDirection === 'rising'    ? '📈 Rising — Bearish' : '→ Flat'}
          </span>
        </div>
      )}

      {/* ── Lead Stocks Card ─────────────────────────────────────────────── */}
      {leadingStocksData && leadingStocksData.stocks && leadingStocksData.stocks.length > 0 && (
        <div className="ts-lead-stocks-card">
          <div className="ts-lead-header">
            <span className="ts-lead-title">Lead Stocks</span>
            <span className={`ts-lead-bias-badge ts-lead-${leadingStocksData.compositeBias?.toLowerCase()}`}>
              {leadingStocksData.compositeBias}
            </span>
            <span className="ts-lead-strength">{leadingStocksData.leadStrength}</span>
            <span className="ts-lead-align">{leadingStocksData.alignmentSummary}</span>
          </div>
          <div className="ts-lead-stocks-grid">
            {leadingStocksData.stocks.map(s => (
              <div key={s.symbol} className={`ts-lead-stock ts-lead-stock-${s.direction?.toLowerCase()}`}>
                <div className="ts-lead-stock-top">
                  <span className="ts-lead-stock-symbol">{s.symbol}</span>
                  <span className={`ts-lead-stock-dir ts-lead-dir-${s.direction?.toLowerCase()}`}>
                    {s.direction === 'BULLISH' ? '▲' : s.direction === 'BEARISH' ? '▼' : '→'}
                    {' '}{s.direction}
                  </span>
                </div>
                <div className="ts-lead-stock-row">
                  <span className="ts-lead-lbl">Price</span>
                  <span className="ts-lead-val">
                    {s.spotPrice > 0 ? `₹${s.spotPrice.toLocaleString('en-IN', { maximumFractionDigits: 1 })}` : '—'}
                    {s.priceChangePct != null && (
                      <span style={{ color: s.priceChangePct >= 0 ? '#00c853' : '#d32f2f', marginLeft: 4 }}>
                        ({s.priceChangePct >= 0 ? '+' : ''}{s.priceChangePct.toFixed(2)}%)
                      </span>
                    )}
                  </span>
                </div>
                {s.futuresPrice > 0 && (
                  <div className="ts-lead-stock-row">
                    <span className="ts-lead-lbl">Fut</span>
                    <span className="ts-lead-val" style={{ color: s.futuresPremium >= 0 ? '#00c853' : '#d32f2f' }}>
                      ₹{s.futuresPrice.toLocaleString('en-IN', { maximumFractionDigits: 1 })}
                      {' '}({s.futuresPremium >= 0 ? '+' : ''}{s.futuresPremium.toFixed(1)})
                    </span>
                  </div>
                )}
                <div className="ts-lead-stock-row">
                  <span className="ts-lead-lbl">OI</span>
                  <span className="ts-lead-val" style={{ fontSize: 10 }}>
                    CE: {s.ceOIMatrix || '—'} | PE: {s.peOIMatrix || '—'}
                  </span>
                </div>
                {s.maxCallOIStrike > 0 && (
                  <div className="ts-lead-stock-row">
                    <span className="ts-lead-lbl">Walls</span>
                    <span className="ts-lead-val" style={{ fontSize: 10 }}>
                      R: {s.maxCallOIStrike} · S: {s.maxPutOIStrike}
                    </span>
                  </div>
                )}
              </div>
            ))}
          </div>
          {leadingStocksData.hasLeadMove && (
            <div className="ts-lead-move-alert">
              ⚡ Lead move detected — heavyweight stocks already moved &gt;0.5%
            </div>
          )}
        </div>
      )}

      {/* ── Buyer Context Row ────────────────────────────────────────────── */}
      <div className="ts-buyer-context">
        {/* Time-of-day zone */}
        <span className={`ts-zone-badge ts-zone-${timeBlock ? 'block' : timeZone === 'OPTIMAL' ? 'good' : 'warn'}`}>
          {timeZone === 'AVOID_OPEN'  ? `🚫 AVOID OPEN (9:15–${openAvoidLabel})`  :
           timeZone === 'AVOID_CLOSE' ? '🚫 AVOID CLOSE (15:10–15:40)' : 'OPTIMAL WINDOW'}
        </span>

        {/* IV Rank pill */}
        {ivRank != null && (
          <span
            className="ts-iv-rank-pill"
            style={{
              background: ivRank < 30 ? '#00c853' : ivRank < 50 ? '#1e88e5' :
                          ivRank < 70 ? '#ff9800' : '#d32f2f',
            }}
            title={ivPercentile != null ? `IV Percentile: ${ivPercentile.toFixed(0)}%` : ''}
          >
            IV Rank {ivRank.toFixed(0)}%
          </span>
        )}

        {/* Buyer-friendly score */}
        {buyerFriendlyScore != null && (
          <span
            className="ts-buyer-score"
            style={{
              color: buyerFriendlyScore >= 7 ? '#00c853' : buyerFriendlyScore >= 5 ? '#ff9800' : '#d32f2f',
            }}
          >
            Buyer Score {buyerFriendlyScore}/10
          </span>
        )}

        {/* Straddle expansion */}
        {straddleExpansionRatio != null && straddleExpansionRatio > 1.1 && (
          <span className="ts-straddle-warn">
            Straddle +{((straddleExpansionRatio - 1) * 100).toFixed(0)}%
          </span>
        )}

        {/* Gap open */}
        {gapOpenPct != null && Math.abs(gapOpenPct) >= 0.5 && (
          <span className="ts-gap-warn">
            Gap {gapOpenPct > 0 ? '+' : ''}{gapOpenPct.toFixed(2)}%
          </span>
        )}
      </div>

      {/* ── PCR Divergence panel ──────────────────────────────────────────── */}
      {pcrDivergence && (
        <div className={`ts-pcr-panel ${pcrDivergence.direction === 'PUT_SURGE' ? 'ts-pcr-panel-bear' : 'ts-pcr-panel-bull'}`}>
          {/* Header: severity badge + direction + opposing tag */}
          <div className="ts-pcr-header">
            <span className="ts-pcr-severity" style={{ background: pcrDivergence.severityColor }}>
              {pcrDivergence.severity}
            </span>
            <span className="ts-pcr-dir">
              {pcrDivergence.direction === 'PUT_SURGE' ? 'PUT SURGE' : 'CALL SURGE'}
              {pcrDivergence.isFastSpike ? ' · INSTANT' : ''}
            </span>
            {pcrDivergence.opposing && (
              <span className="ts-pcr-oppose-tag">OPPOSES {dir?.toUpperCase()}</span>
            )}
          </div>

          {/* From → To with % change and level label */}
          <div className="ts-pcr-values">
            <span className="ts-pcr-from">{pcrDivergence.fromVal.toFixed(2)}</span>
            <span className="ts-pcr-arrow">→</span>
            <span className="ts-pcr-to" style={{ color: pcrDivergence.severityColor }}>
              {pcrDivergence.current.toFixed(2)}
            </span>
            <span className="ts-pcr-pct" style={{ color: pcrDivergence.severityColor }}>
              ({pcrDivergence.primaryChange > 0 ? '+' : ''}{pcrDivergence.primaryChange.toFixed(2)} · {pcrDivergence.pctChange.toFixed(0)}%)
            </span>
            <span className="ts-pcr-level">{pcrDivergence.levelLabel}</span>
          </div>

          {/* Mini PCR trend — last 6 values, current underlined */}
          <div className="ts-pcr-trend">
            <span className="ts-pcr-trend-label">Trend:</span>
            {pcrDivergence.trend.map((v, i) => (
              <span
                key={i}
                className={`ts-pcr-tick ${i === pcrDivergence.trend.length - 1 ? 'ts-pcr-tick-now' : ''}`}
                style={{
                  color: v > 1.3 ? '#b71c1c' : v > 1.1 ? '#e65100' : v < 0.8 ? '#2e7d32' : '#666',
                  fontWeight: i === pcrDivergence.trend.length - 1 ? 800 : 400,
                }}
              >
                {v.toFixed(2)}
              </span>
            ))}
          </div>

          {/* Trading implication */}
          <div className="ts-pcr-implication">
            {pcrDivergence.direction === 'PUT_SURGE'
              ? (pcrDivergence.severity === 'SEVERE'
                  ? 'Large institutional put buying detected — strong bearish signal. Avoid new call entries.'
                  : 'Elevated put activity — bearish pressure building.')
              : (pcrDivergence.severity === 'SEVERE'
                  ? 'Large institutional call buying detected — strong bullish signal. Avoid new put entries.'
                  : 'Elevated call activity — bullish pressure building.')}
            {pcrDivergence.opposing && (
              <span className="ts-pcr-oppose-warn">
                {` Entering ${dir?.toUpperCase()} against this divergence carries significant risk.`}
              </span>
            )}
          </div>
        </div>
      )}

      {/* ── Max Pain row ─────────────────────────────────────────────────── */}
      {maxPainPrice && (
        <div className="ts-max-pain-row">
          <span className="ts-max-pain-label">Max Pain</span>
          <span className="ts-max-pain-value">{Math.round(maxPainPrice)}</span>
          {maxPainDiffPct != null && (
            <span className={`ts-max-pain-delta ${maxPainDiffPct >= 0 ? 'ts-mp-above' : 'ts-mp-below'}`}>
              {maxPainDiffPct > 0 ? '+' : ''}{maxPainDiffPct.toFixed(1)}% from spot
            </span>
          )}
        </div>
      )}
      {maxPainWarning && (
        <div className="ts-max-pain-warn">{maxPainWarning}</div>
      )}

      {/* ── S/R proximity warning / block ────────────────────────────────── */}
      {srBlocked && (
        <div className="ts-sr-block">
          BLOCKED — {dir === 'call' ? `Resistance at ${resistance}` : `Support at ${support}`} is within {Math.round(srThreshold)} pts of spot.
          {dir === 'call'
            ? ' Entering a CALL here puts you against heavy writers at the wall. Wait for a candle CLOSE above the level.'
            : ' Entering a PUT here puts you against strong buyers at the floor. Wait for a candle CLOSE below the level.'}
        </div>
      )}
      {!srBlocked && srWarning && (
        <div className="ts-sr-warn">{srWarning}</div>
      )}

      {/* ── Gamma Blast Zone alert ───────────────────────────────────────── */}
      {gammaBlastZone && (
        <div className="ts-gamma-blast-alert">
          ⚠️ GAMMA BLAST ZONE — Expiry day, &lt;60 min to close. Gamma is exponential.
          Avoid new option buys. Square off open positions.
        </div>
      )}
      {!gammaBlastZone && daysToExpiry === 0 && (
        <div className="ts-expiry-day-note">
          Expiry day — theta decay accelerating. Prefer closing over new entries.
        </div>
      )}

      {/* ── Confluence Meter ─────────────────────────────────────────────── */}
      <div className="ts-confluence-section">
        <div className="ts-confluence-row">
          <span className="ts-confluence-label">Early Trend</span>
          <span
            className="ts-confluence-dir"
            style={{
              background:
                earlyTrendDirection === 'CALL' ? '#00c853' :
                earlyTrendDirection === 'PUT'  ? '#d32f2f' :
                '#9e9e9e',
            }}
          >
            {earlyTrendDirection}
          </span>
          <span className="ts-confluence-strength">{trendStrength}</span>
          <span className="ts-confluence-score">{confluenceScore}/10</span>
        </div>
        <div className="ts-confluence-bar-track">
          <div
            className="ts-confluence-bar-fill"
            style={{
              width: `${confluenceScore * 10}%`,
              background:
                confluenceScore >= 8 ? '#00c853' :
                confluenceScore >= 6 ? '#1e88e5' :
                confluenceScore >= 4 ? '#ff9800' :
                '#9e9e9e',
            }}
          />
        </div>
      </div>


      {/* ── Historical Performance Context ───────────────────────────────── */}
      {histStats != null && (
        <div className={`ts-perf-ctx ${histStats.wr < 40 ? 'ts-perf-ctx-warn' : histStats.wr >= 55 ? 'ts-perf-ctx-good' : 'ts-perf-ctx-neutral'}`}>
          <div className="ts-perf-ctx-title">
            Historical Performance — {symbol} ({histStats.total} closed trades)
            {adaptiveDowngrade && (
              <span className="ts-perf-ctx-downgrade-tag">TAKE TRADE skipped</span>
            )}
          </div>
          <div className="ts-perf-ctx-grid">
            <div className="ts-perf-ctx-cell">
              <span className="ts-perf-ctx-label">Overall W/L</span>
              <span className="ts-perf-ctx-val" style={{ color: histStats.wr >= 50 ? '#00c853' : '#d32f2f' }}>
                {histStats.wins}W / {histStats.total - histStats.wins}L · {histStats.wr}%
              </span>
            </div>
            {histStats.ttCount >= 3 && histStats.ttWR != null && (
              <div className="ts-perf-ctx-cell">
                <span className="ts-perf-ctx-label">Take Trade ({histStats.ttCount})</span>
                <span className="ts-perf-ctx-val" style={{ color: histStats.ttWR >= 50 ? '#00c853' : histStats.ttWR < 40 ? '#d32f2f' : '#ff9800' }}>
                  {histStats.ttWins}W/{histStats.ttCount - histStats.ttWins}L · {histStats.ttWR}%
                </span>
              </div>
            )}
            {histStats.orCount >= 3 && histStats.orWR != null && (
              <div className="ts-perf-ctx-cell">
                <span className="ts-perf-ctx-label">On Risk ({histStats.orCount})</span>
                <span className="ts-perf-ctx-val" style={{ color: histStats.orWR >= 50 ? '#00c853' : histStats.orWR < 30 ? '#d32f2f' : '#ff9800' }}>
                  {histStats.orWins}W/{histStats.orCount - histStats.orWins}L · {histStats.orWR}%
                </span>
              </div>
            )}
            {histStats.emaCount >= 1 && histStats.emaWR != null && (
              <div className="ts-perf-ctx-cell">
                <span className="ts-perf-ctx-label">EMA Trend ({histStats.emaCount})</span>
                <span className="ts-perf-ctx-val" style={{ color: histStats.emaWR >= 50 ? '#00c853' : histStats.emaWR < 30 ? '#d32f2f' : '#ff9800' }}>
                  {histStats.emaWins}W/{histStats.emaCount - histStats.emaWins}L · {histStats.emaWR}%
                </span>
              </div>
            )}
            {histStats.avgPnl != null && (
              <div className="ts-perf-ctx-cell">
                <span className="ts-perf-ctx-label">Avg P&amp;L</span>
                <span className="ts-perf-ctx-val" style={{ color: histStats.avgPnl >= 0 ? '#00c853' : '#d32f2f' }}>
                  {histStats.avgPnl >= 0 ? '+' : ''}{histStats.avgPnl} pts/trade
                </span>
              </div>
            )}
          </div>
          {adaptiveDowngrade && (
            <div className="ts-perf-ctx-note">
              TAKE TRADE entries skipped — {histStats.ttCount} past TT signals on {symbol} show only {histStats.ttWR}% win rate (need ≥40%). No new entries for this symbol until win rate recovers.
            </div>
          )}
        </div>
      )}

      {/* ── Expiry Selection Guidance (collapsible) ───────────────────── */}
      {expiryGuide && (
        <div className="ts-expiry-guide">
          {/* Always-visible summary bar */}
          <div className="ts-section-collapsed-bar">
            <div className="ts-section-collapsed-left">
              <span className="ts-expiry-guide-title">Expiry Selection</span>
              <span className="ts-expiry-verdict-badge" style={{ background: expiryGuide.color }}>
                {expiryGuide.type} · {expiryGuide.dte} DTE
              </span>
              {expiryGuide.holdCarryPct != null ? (
                <span
                  className="ts-expiry-inline-carry"
                  style={{
                    color: expiryGuide.holdCarryPct > 4.0 ? '#b71c1c'
                         : expiryGuide.holdCarryPct > 2.5 ? '#e65100'
                         : '#2e7d32',
                  }}
                >
                  {expiryGuide.holdCarryPct.toFixed(1)}% carry / {THETA_HOLD_MINUTES} min
                </span>
              ) : null}
            </div>
            <button
              className="ts-collapse-toggle"
              onClick={() => setShowExpiryDetail(s => !s)}
            >
              {showExpiryDetail ? 'Hide ▴' : 'Details ▾'}
            </button>
          </div>

          {/* Expanded detail */}
          {showExpiryDetail && (
            <>
              {/* Theta breakdown grid */}
              {expiryGuide.thetaPerDay != null && (
                <div className="ts-expiry-theta-grid">
                  <div className="ts-expiry-theta-cell">
                    <span className="ts-expiry-theta-label">Theta/day</span>
                    <span className="ts-expiry-theta-val" style={{ color: '#e65100' }}>
                      −₹{expiryGuide.thetaPerDay.toFixed(1)}
                    </span>
                  </div>
                  <div className="ts-expiry-theta-cell">
                    <span className="ts-expiry-theta-label">Carry cost</span>
                    <span
                      className="ts-expiry-theta-val"
                      style={{
                        color: (expiryGuide.sideThetaPct ?? 0) > 2.5 ? '#b71c1c'
                             : (expiryGuide.sideThetaPct ?? 0) > 1.5 ? '#e65100'
                             : '#2e7d32',
                      }}
                    >
                      {expiryGuide.sideThetaPct != null
                        ? `${expiryGuide.sideThetaPct.toFixed(1)}%/day`
                        : '—'}
                    </span>
                  </div>
                  <div className="ts-expiry-theta-cell">
                    <span className="ts-expiry-theta-label">Total burden</span>
                    <span className="ts-expiry-theta-val" style={{ color: '#c62828' }}>
                      {expiryGuide.totalThetaBurden != null
                        ? `−₹${Math.round(expiryGuide.totalThetaBurden)}`
                        : '—'}
                    </span>
                  </div>
                </div>
              )}

              {/* DTE ladder */}
              <div className="ts-dte-ladder">
                {[
                  { label: '0–2 DTE', tag: 'SAME/NEXT DAY', color: '#b71c1c', note: 'Scalp only — theta gate decides' },
                  { label: '3–7 DTE', tag: 'WEEKLY',        color: '#2e7d32', note: 'Best for intraday — ATM / 1 ITM' },
                  { label: '8–14 DTE', tag: 'NEAR-MONTHLY', color: '#f9a825', note: 'OK — more vega, less gamma' },
                  { label: '15+ DTE', tag: 'MONTHLY',       color: '#e65100', note: 'Vega-heavy — ATM / 1 ITM only' },
                ].map(rung => {
                  const active =
                    (rung.label === '0–2 DTE'  && expiryGuide.dte <= 2) ||
                    (rung.label === '3–7 DTE'  && expiryGuide.dte >= 3 && expiryGuide.dte <= 7) ||
                    (rung.label === '8–14 DTE' && expiryGuide.dte >= 8 && expiryGuide.dte <= 14) ||
                    (rung.label === '15+ DTE'  && expiryGuide.dte >= 15);
                  return (
                    <div key={rung.label} className={`ts-dte-rung ${active ? 'ts-dte-rung-active' : ''}`}
                      style={{ borderLeftColor: active ? rung.color : '#ddd' }}>
                      <span className="ts-dte-rung-label">{rung.label}</span>
                      <span className="ts-dte-rung-tag" style={{ color: active ? rung.color : '#aaa' }}>{rung.tag}</span>
                      <span className="ts-dte-rung-note">{rung.note}</span>
                    </div>
                  );
                })}
              </div>

              {/* Recommendation */}
              <div className={`ts-expiry-rec ${expiryGuide.dte > 14 || expiryGuide.dte === 0 ? 'ts-expiry-rec-warn' : 'ts-expiry-rec-ok'}`}>
                {expiryGuide.rec}
              </div>
            </>
          )}
        </div>
      )}

      {/* ── Strike Selection (collapsible) ────────────────────────────── */}
      {strikeOptions && (
        <div className="ts-strike-table">
          {/* Always-visible summary bar */}
          <div className="ts-section-collapsed-bar">
            <div className="ts-section-collapsed-left">
              <span className="ts-strike-table-title">Strike — {strikeOptions.side}</span>
              {(() => {
                const recKey  = strikeOptions.recommended.toLowerCase();
                const recData = strikeOptions[recKey];
                if (!recData) return null;
                const delta = dir === 'call' ? recData.callDelta : Math.abs(recData.putDelta ?? 0);
                const ltp   = dir === 'call' ? recData.ceLTP    : recData.peLTP;
                return (
                  <span className="ts-strike-summary-tag">
                    {strikeOptions.recommended} {recData.strikePrice} · Δ {delta.toFixed(2)}{ltp != null ? ` · ₹${ltp.toFixed(0)}` : ''}
                  </span>
                );
              })()}
            </div>
            <button
              className="ts-collapse-toggle"
              onClick={() => setShowStrikeDetail(s => !s)}
            >
              {showStrikeDetail ? 'Hide ▴' : 'All strikes ▾'}
            </button>
          </div>

          {/* Expanded detail */}
          {showStrikeDetail && (
            <>
              {[
                {
                  key: 'ITM',
                  data: strikeOptions.itm,
                  deltaFn: s => dir === 'call' ? s.callDelta : Math.abs(s.putDelta),
                  ltpFn:   s => dir === 'call' ? s.ceLTP : s.peLTP,
                  note: 'Safer · high cost · lower % return',
                },
                {
                  key: 'ATM',
                  data: strikeOptions.atm,
                  deltaFn: s => dir === 'call' ? s.callDelta : Math.abs(s.putDelta),
                  ltpFn:   s => dir === 'call' ? s.ceLTP : s.peLTP,
                  note: 'Balanced · best risk/reward',
                },
                {
                  key: 'OTM',
                  data: strikeOptions.otm,
                  deltaFn: s => dir === 'call' ? s.callDelta : Math.abs(s.putDelta),
                  ltpFn:   s => dir === 'call' ? s.ceLTP : s.peLTP,
                  note: 'High return · needs big move · ~30% win',
                },
              ].map(({ key, data, deltaFn, ltpFn, note }) => data ? (
                <div key={key} className={`ts-strike-row-opt ${key === strikeOptions.recommended ? 'ts-strike-row-rec' : ''}`}>
                  <span className={`ts-strike-type-badge ts-strike-${key.toLowerCase()}`}>{key}</span>
                  <span className="ts-strike-sp">{data.strikePrice}</span>
                  <span className="ts-strike-delta">Δ {deltaFn(data).toFixed(2)}</span>
                  <span className="ts-strike-ltp">
                    {ltpFn(data) != null ? `₹${ltpFn(data).toFixed(0)}` : '—'}
                  </span>
                  <span className="ts-strike-note-text">{note}</span>
                  {key === strikeOptions.recommended && (
                    <span className="ts-strike-pick-badge">PICK</span>
                  )}
                </div>
              ) : null)}

              <div className="ts-strike-dte-rule">
                DTE rule ({strikeOptions.dte}d):&nbsp;
                {strikeOptions.dte <= 2
                  ? 'Short DTE — ATM only, no time for OTM recovery'
                  : strikeOptions.dte <= 7
                  ? 'Weekly — ATM gives best delta/theta balance'
                  : '8+ DTE — 1-OTM acceptable, enough time to drift ITM'}
              </div>
            </>
          )}
        </div>
      )}

      {/* ── Multi-timeframe confirmation ─────────────────────────────────── */}
      {emaData && (
        <div className="ts-mtf-block">
          <div className="ts-mtf-title">Multi-Timeframe</div>

          {/* 5-min row */}
          <div className="ts-mtf-row">
            <span className="ts-mtf-tf">5-min</span>
            <span className={`ts-mtf-badge ${emaData.priceAboveEma ? 'ts-mtf-bull' : 'ts-mtf-bear'}`}>
              {emaData.priceAboveEma ? '↑ BULL' : '↓ BEAR'}
            </span>
            <span className="ts-mtf-val">EMA20 {emaData.ema20 != null ? emaData.ema20.toFixed(0) : '—'}</span>
          </div>

          {/* 15-min row */}
          <div className={`ts-mtf-row ${htfConflict ? 'ts-mtf-row-conflict' : ''}`}>
            <span className="ts-mtf-tf">15-min</span>
            {htfRegime && htfRegime !== 'NOT_READY' ? (
              <>
                <span className={`ts-mtf-badge ${emaData.priceAboveEma65 ? 'ts-mtf-bull' : 'ts-mtf-bear'}`}>
                  {emaData.priceAboveEma65 ? '↑ BULL' : '↓ BEAR'}
                </span>
                <span className="ts-mtf-val">EMA9 {emaData.ema65 != null ? emaData.ema65.toFixed(0) : '—'}</span>
              </>
            ) : (
              <span className="ts-mtf-loading">collecting {emaData.barsComputed15m || 0}/9 bars</span>
            )}
          </div>

          {/* Daily bias row */}
          <div className={`ts-mtf-row ${dailyConflict ? 'ts-mtf-row-conflict' : ''}`}>
            <span className="ts-mtf-tf">Daily</span>
            {dailyBias ? (
              <span className={`ts-mtf-badge ${dailyBias === 'BULLISH' ? 'ts-mtf-bull' : 'ts-mtf-bear'}`}>
                {dailyBias === 'BULLISH' ? '↑ BULL' : '↓ BEAR'}
              </span>
            ) : (
              <span className="ts-mtf-loading">waiting for open tick</span>
            )}
          </div>

          {/* Alignment verdict */}
          {dir && htfRegime && htfRegime !== 'NOT_READY' && (
            <div className={`ts-mtf-verdict ${
              fullAlignment ? 'ts-mtf-verdict-strong' :
              htfConflict || dailyConflict ? 'ts-mtf-verdict-warn' :
              'ts-mtf-verdict-partial'
            }`}>
              {fullAlignment
                ? `All 3 TFs ${dir === 'call' ? 'BULLISH' : 'BEARISH'} — highest conviction`
                : htfConflict
                ? '5-min vs 15-min conflict — reduce size by 50%'
                : dailyConflict
                ? `Daily bias opposes ${dir === 'call' ? 'CALL' : 'PUT'} — trade with caution`
                : 'Partial alignment — trade only if 15-min agrees'}
            </div>
          )}
        </div>
      )}

      {/* ── Entry Confirmation Candle Rule (collapsible) ──────────────────── */}
      {finalVerdict === 'TAKE TRADE' && (
        <div className="ts-conf-panel">
          {/* Always-visible summary row */}
          <div className="ts-conf-summary">
            <div className="ts-conf-summary-left">
              <span className="ts-conf-title">
                Entry Confirmation · {dir === 'call' ? 'CALL' : 'PUT'} · 5-min candle
              </span>
            </div>
            <button
              className="ts-collapse-toggle"
              onClick={() => setShowCandleSteps(s => !s)}
            >
              {showCandleSteps ? 'Hide steps ▴' : 'Show steps ▾'}
            </button>
          </div>

          {/* One-line watch-level — always visible */}
          <div className="ts-conf-summary-level">
            {emaData?.ema20 != null
              ? `Wait for candle CLOSE ${dir === 'call' ? 'above' : 'below'} EMA20 ${Math.round(emaData.ema20)} — then enter at next candle open`
              : spotPrice != null
              ? `Wait for candle CLOSE ${dir === 'call' ? 'above current high' : 'below current low'} — then enter at next candle open`
              : `Wait for 5-min candle close confirmation before entering`}
          </div>

          {/* Expanded detail — hidden by default */}
          {showCandleSteps && (
            <>
              {/* Signal fired → awaiting */}
              <div className="ts-conf-signal-row">
                <span className="ts-conf-signal-fired">Signal fired</span>
                <span className="ts-conf-arrow">→</span>
                <span className="ts-conf-await">Do NOT enter yet — wait for confirmation</span>
              </div>

              {/* Key level to watch */}
              {emaData?.ema20 != null ? (
                <div className="ts-conf-level">
                  <span className="ts-conf-level-label">Watch level</span>
                  <span className="ts-conf-level-val">EMA20: {Math.round(emaData.ema20)}</span>
                  <span className="ts-conf-level-action">
                    Need candle CLOSE {dir === 'call' ? 'above' : 'below'} this
                  </span>
                </div>
              ) : spotPrice != null ? (
                <div className="ts-conf-level">
                  <span className="ts-conf-level-label">Watch level</span>
                  <span className="ts-conf-level-val">Spot: {Math.round(spotPrice)}</span>
                  <span className="ts-conf-level-action">
                    Need candle CLOSE {dir === 'call' ? 'above current high' : 'below current low'}
                  </span>
                </div>
              ) : null}

              {/* 4-step process */}
              <div className="ts-conf-steps">
                {[
                  {
                    text: `Mark the ${dir === 'call' ? 'high' : 'low'} of the current signal candle`,
                    sub:  'This is your reference price — the bar that printed the signal',
                  },
                  {
                    text: `Wait for the NEXT candle to CLOSE ${dir === 'call' ? 'above that high' : 'below that low'}`,
                    sub:  'A wick touch is NOT enough — only a full candle close counts',
                  },
                  {
                    text: 'Enter at the open of the candle AFTER the confirmation close',
                    sub:  'Never buy in the middle of a candle — wait for the new bar to open',
                  },
                  {
                    text: dir === 'call'
                      ? 'If the candle closes RED or below EMA20 — setup failed, do not enter'
                      : 'If the candle closes GREEN or above EMA20 — setup failed, do not enter',
                    sub:  'One valid attempt only — missed confirmation = fresh signal required',
                  },
                ].map((step, i) => (
                  <div key={i} className="ts-conf-step">
                    <span className="ts-conf-step-n">{i + 1}</span>
                    <div className="ts-conf-step-body">
                      <span className="ts-conf-step-text">{step.text}</span>
                      <span className="ts-conf-step-sub">{step.sub}</span>
                    </div>
                  </div>
                ))}
              </div>

              {/* Never rules */}
              <div className="ts-conf-dont-block">
                <span className="ts-conf-dont-title">Never</span>
                <span className="ts-conf-dont-item">enter on the signal candle itself</span>
                <span className="ts-conf-dont-sep">·</span>
                <span className="ts-conf-dont-item">chase a move already in progress</span>
                <span className="ts-conf-dont-sep">·</span>
                <span className="ts-conf-dont-item">enter mid-candle</span>
              </div>
            </>
          )}
        </div>
      )}


      {/* ── Signal confirmation / auto-tracking / cooldown indicators ──────── */}
      {isAutoTracking && autoTradeRef.current && (() => {
        const trade        = autoTradeRef.current;
        const currentLTP   = getStrikeLTP(trade.dir, trade.entryStrike);
        // Backend exit plan (RiskPlan): underlying stop, R, 2R target, premium backstop.
        const riskPts      = trade.riskPts ?? null;
        const uStop        = trade.underlyingStop ?? null;
        const backstop     = trade.entryLTP != null ? trade.entryLTP * (1 - (trade.slPct ?? 0.30)) : null;
        const stopDistPts  = (uStop != null && spotPrice != null)
          ? (trade.dir === 'call' ? spotPrice - uStop : uStop - spotPrice) : null;
        const unrealisedPts = (currentLTP != null && trade.entryLTP != null)
          ? currentLTP - trade.entryLTP : null;
        const unrealisedRs  = unrealisedPts != null
          ? Math.round(unrealisedPts * (trade.allocatedLotSize ?? trade.lotSize ?? 50))
          : null;
        const entryTime = (() => {
          try { return new Date(trade.timestamp).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true }); }
          catch { return ''; }
        })();
        // Progress in R: bar spans −1.5R (premium R stop) … +2R (target).
        const rMult    = (unrealisedPts != null && riskPts > 0) ? unrealisedPts / riskPts : null;
        const rBarPct  = rMult == null ? null : Math.max(0, Math.min(100, (rMult + 1.5) / 3.5 * 100));
        const rColor   = rMult == null ? '#9e9e9e' : rMult >= 0 ? '#00c853' : rMult > -1 ? '#ff9800' : '#ef5350';
        const isProfitable = unrealisedPts != null && unrealisedPts > 0;

        return (
          <div className="ts-live-trade-widget">
            {/* Row 1: direction badge, strike, lot-count tag, entry time */}
            <div className="ts-ltw-header">
              <span className="ts-ltw-dot" />
              <span className="ts-ltw-label">LIVE</span>
              <span className={`ts-ltw-dir ${trade.dir === 'call' ? 'ts-ltw-call' : 'ts-ltw-put'}`}>
                {trade.dir === 'call' ? 'CE' : 'PE'} {trade.entryStrike ?? '—'}
              </span>
              {(() => {
                const numLots   = trade.numLots ?? 1;
                const fullQty   = (trade.lotSize ?? 50) * numLots;
                const isOnRiskT = trade.finalVerdict === 'ON RISK';
                const allocQty  = trade.allocatedLotSize ?? fullQty;
                // ON RISK trades 1 lot, TAKE TRADE trades 2 — both whole, tradable
                // quantities, so the count itself (not a fractional "½" suffix) is the
                // signal; only the badge color distinguishes conviction tier.
                return (
                  <span className={isOnRiskT ? 'ts-ltw-half' : 'ts-ltw-full'}>
                    {numLots > 1 ? `${numLots} lots` : '1 lot'} · {allocQty} qty
                  </span>
                );
              })()}
              <span className="ts-ltw-time">{entryTime}</span>
            </div>

            {/* Row 2: entry LTP → live LTP, unrealised P&L */}
            <div className="ts-ltw-ltp-row">
              <span className="ts-ltw-entry">
                Entry {trade.entryLTP != null ? `₹${trade.entryLTP.toFixed(1)}` : '—'}
              </span>
              <span className="ts-ltw-arrow">→</span>
              <span className="ts-ltw-live">
                {currentLTP != null ? `₹${currentLTP.toFixed(1)}` : 'LTP —'}
              </span>
              {unrealisedPts != null && (
                <span className={`ts-ltw-pnl ${isProfitable ? 'ts-ltw-profit' : 'ts-ltw-loss'}`}>
                  {unrealisedPts >= 0 ? '+' : ''}{unrealisedPts.toFixed(1)} pts
                  {unrealisedRs != null && (
                    <> · {unrealisedRs >= 0 ? '+' : '−'}₹{Math.abs(unrealisedRs).toLocaleString('en-IN')}</>
                  )}
                </span>
              )}
            </div>

            {/* Row 3: backend exit plan — underlying stop, R, target, backstop */}
            {riskPts != null ? (
              <div className="ts-ltw-stop-row">
                <span className="ts-ltw-stop">
                  Stop {trade.dir === 'call' ? '≤' : '≥'} {uStop != null ? Math.round(uStop) : '—'}
                  {stopDistPts != null && ` (${Math.round(stopDistPts)} pts away)`}
                  {trade.trailing || trade.targetHit ? ' · trailing' : ''}
                </span>
                <span className="ts-ltw-peak">
                  R ₹{riskPts.toFixed(1)} · Target ₹{trade.targetLTP != null ? trade.targetLTP.toFixed(1) : '—'}
                  {backstop != null && ` · Backstop ₹${backstop.toFixed(1)}`}
                </span>
                {rBarPct != null && (
                  <>
                    <div className="ts-ltw-cushion-track">
                      <div className="ts-ltw-cushion-fill" style={{ width: `${rBarPct}%`, background: rColor }} />
                    </div>
                    <span className="ts-ltw-cushion-pct" style={{ color: rColor }}>
                      {rMult >= 0 ? '+' : ''}{rMult.toFixed(2)}R
                    </span>
                  </>
                )}
              </div>
            ) : (
              <div className="ts-ltw-stop-row">
                <span className="ts-ltw-stop">
                  Backstop ₹{backstop != null ? backstop.toFixed(1) : '—'} (position opened before the exit plan existed)
                </span>
              </div>
            )}
          </div>
        );
      })()}

      {/* ── 4-Factor Entry Gate ──────────────────────────────────────────── */}
      <div className="ts-gate">
        <div className="ts-gate-title">4-Factor Entry Gate</div>
        <div className="ts-gate-factors">
          {fourFactors.map(f => (
            <div key={f.key} className={`ts-factor ${f.met ? 'ts-factor-met' : 'ts-factor-unmet'}`} title={f.detail}>
              <span className="ts-factor-icon">{f.met ? '✅' : '❌'}</span>
              <span className="ts-factor-label">{f.label}</span>
            </div>
          ))}
        </div>

        <div className="ts-verdict" style={{ background: finalVerdictColor }}>
          {finalVerdict === 'CIRCUIT BREAK' && 'CIRCUIT BREAKER — ALL SIGNALS LOCKED'}
          {finalVerdict === 'DAILY LIMIT'   && `DAILY LIMIT — ${verdict === 'TAKE TRADE' || verdict === 'ON RISK' ? `${verdict} ${dir?.toUpperCase()} signal blocked` : 'no new entries today'}`}
          {finalVerdict === 'PROFIT LOCKED' && `PROFIT LOCKED — the day's goal is banked${backendSystemStatus?.dayNetPctOfCapital != null ? ` (${backendSystemStatus.dayNetPctOfCapital >= 0 ? '+' : ''}${backendSystemStatus.dayNetPctOfCapital}%)` : ''}, no new entries today`}
          {finalVerdict === 'FEED FALLBACK' && `FEED FALLBACK — ${verdict === 'TAKE TRADE' || verdict === 'ON RISK' ? `${verdict} ${dir?.toUpperCase()} signal blocked` : 'entries paused until Kite is back'}`}
          {finalVerdict === 'TAKE TRADE' && 'ALL 4 ALIGNED — TAKE TRADE'}
          {finalVerdict === 'SKIP'       && (timeBlock
            ? (timeZone === 'AVOID_OPEN' ? `SKIP — AVOID OPEN (9:15–${openAvoidLabel}): wait for ${openAvoidLabel} confirmation` : 'SKIP — AVOID CLOSE (15:10–15:40): no new entries')
            : srBlocked
              ? `SKIP — At ${dir === 'call' ? 'resistance' : 'support'}, wait for level break before entering`
              : premiumConflictBlock
                ? `SKIP — Premium signal (${premiumRec || 'NO TRADE'}) doesn't confirm ${dir?.toUpperCase()}`
              : pcrOpposingBlock
                ? `SKIP — PCR ${pcrDivergence.direction === 'PUT_SURGE' ? 'PUT surge' : 'CALL surge'} ${pcrDivergence.fromVal.toFixed(2)}→${pcrDivergence.current.toFixed(2)} opposes signal`
                : `SKIP — ${missingKeys.join(', ')} not confirmed`)}
          {finalVerdict === 'BALANCED'   && 'BALANCED MARKET — No clear bias, skip trade'}
          {finalVerdict === 'NO SETUP'   && 'NO SETUP — Insufficient directional confluence'}
        </div>
      </div>

      {/* ── Balanced market note ─────────────────────────────────────────── */}
      {isBalanced && (
        <div className="ts-balanced-note">
          Option chain shows balanced OI on both sides. Large part of improving win rate is
          <strong> avoiding trades in balanced markets.</strong>
        </div>
      )}

      {/* ── NO TRADE reasons ─────────────────────────────────────────────────
          Gated by finalVerdict so this panel can never contradict the hero banner
          above. TAKE TRADE means the checklist genuinely cleared every gate — hiding
          the panel entirely there stops the old "TAKE TRADE hero + NO TRADE list"
          contradiction. ON RISK still shows a trade being taken (1 lot), so the
          header is reworded to "caution factors" instead of falsely claiming
          "NO TRADE" while a position is actually being recommended. */}
      {allNoTradeReasons.length > 0 && finalVerdict !== 'TAKE TRADE' && (
        <div className="ts-no-trade-list">
          <div className="ts-no-trade-header">
            {finalVerdict === 'ON RISK'
              ? `${allNoTradeReasons.length} caution factor${allNoTradeReasons.length > 1 ? 's' : ''} — reduced conviction (ON RISK)`
              : `NO TRADE — ${allNoTradeReasons.length} blocking condition${allNoTradeReasons.length > 1 ? 's' : ''}`}
          </div>
          {(showAllReasons ? allNoTradeReasons : allNoTradeReasons.slice(0, 3)).map((reason, i) => (
            <div key={i} className="ts-no-trade-item">
              <span className="ts-no-trade-dot">●</span>
              {reason}
            </div>
          ))}
          {allNoTradeReasons.length > 3 && (
            <button
              className="ts-no-trade-more"
              onClick={() => setShowAllReasons(s => !s)}
            >
              {showAllReasons
                ? '▴ Show less'
                : `+ ${allNoTradeReasons.length - 3} more`}
            </button>
          )}
        </div>
      )}

      {/* ── Chain Sweep banner ───────────────────────────────────────────── */}
      {(chainSweepBull || chainSweepBear) && (
        <div className={`ts-chain-sweep ${chainSweepBull ? 'ts-chain-sweep-bull' : 'ts-chain-sweep-bear'}`}>
          <span className="ts-chain-sweep-icon">⚡</span>
          <div className="ts-chain-sweep-body">
            <span className="ts-chain-sweep-title">
              {chainSweepBull ? 'BULLISH CHAIN SWEEP' : 'BEARISH CHAIN SWEEP'}
            </span>
            <span className="ts-chain-sweep-detail">
              {chainSweepBull
                ? `${chainSweepData.bullPct}% of strikes (${chainSweepData.bullCount}/${chainSweepData.total}) show CE Long Build-up + PE Short Build-up — institutional conviction of upward breakout`
                : `${chainSweepData.bearPct}% of strikes (${chainSweepData.bearCount}/${chainSweepData.total}) show PE Long Build-up + CE Short Build-up — institutional conviction of downward breakout`}
            </span>
          </div>
        </div>
      )}

      {/* ── 6-condition columns ──────────────────────────────────────────── */}
      <div className="ts-columns">
        <div className="ts-col">
          <div className="ts-col-header ts-call-header">
            Call Buy <span className="ts-score">{callScore}/{callEval}</span>
          </div>
          {callConditions.map((c, i) => (
            <div key={i} className={`ts-row ${c.met ? 'ts-row-met' : 'ts-row-unmet'}`}>
              <span className="ts-icon">{c.met ? '✅' : '❌'}</span>
              <div className="ts-row-body">
                <span className="ts-row-label">{c.label}</span>
                <span className="ts-row-detail">{c.detail}</span>
              </div>
            </div>
          ))}
        </div>

        <div className="ts-col">
          <div className="ts-col-header ts-put-header">
            Put Buy <span className="ts-score">{putScore}/{putEval}</span>
          </div>
          {putConditions.map((c, i) => (
            <div key={i} className={`ts-row ${c.met ? 'ts-row-met' : 'ts-row-unmet'}`}>
              <span className="ts-icon">{c.met ? '✅' : '❌'}</span>
              <div className="ts-row-body">
                <span className="ts-row-label">{c.label}</span>
                <span className="ts-row-detail">{c.detail}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* ── Position Sizing Guide ───────────────────────────────────────────
          Only two tiers — Indian F&O lots can't be split, so there is no tradeable
          reduced-size tier. Anything short of full 4/4-factor conviction is a skip. */}
      <div className="ts-sizing-section">
        <div className="ts-sizing-title">Position Sizing by Conviction</div>
        <div className="ts-sizing-rows">
          {[
            { verdict: 'TAKE TRADE', size: '1 full lot', color: '#00c853' },
            { verdict: 'Anything else (ON RISK / BIAS / NO SETUP / BALANCED)', size: '0 — skip', color: '#9e9e9e' },
          ].map(row => (
            <div
              key={row.verdict}
              className={`ts-sizing-row ${
                (row.verdict === 'TAKE TRADE') === (finalVerdict === 'TAKE TRADE') ? 'ts-sizing-active' : ''
              }`}
            >
              <span className="ts-sizing-label" style={{ color: row.color }}>{row.verdict}</span>
              <span className="ts-sizing-value">{row.size}</span>
            </div>
          ))}
        </div>
        <div className="ts-sizing-rules">
          <span>No averaging down on losing positions</span>
          <span>One directional trade at a time</span>
          <span>SL: 0DTE 15% · 1-2d 20% · 3-7d 25% · 8+d 30%</span>
          <span>Trail stop activates after +5% move from entry</span>
        </div>
      </div>

      {/* ── Session P&L Tracker ──────────────────────────────────────────── */}
      <div className="ts-session-tracker">
        <div className="ts-session-header">
          <span className="ts-session-title">
            Today's SYSTEM P&amp;L (backend)
          </span>
          {sessionWinRate != null && (
            <span
              className="ts-session-winrate"
              style={{ color: sessionWinRate >= 50 ? '#00c853' : '#d32f2f' }}
            >
              {sessionWinRate}% ({sessionTrades.wins}W / {sessionTrades.losses}L)
            </span>
          )}
        </div>
        <div className="ts-session-mirror-note">
          Mirrored from the backend's autonomous SYSTEM engine — not editable here.
        </div>
        {!circuitBreaker && sessionTrades.losses === 1 && (
          <div className="ts-one-loss-warn">
            1 loss today — one more triggers the circuit breaker
          </div>
        )}
        {circuitBreaker && (
          <div className="ts-one-loss-warn" style={{ color: '#b71c1c' }}>
            Circuit breaker active on the backend — releases automatically after cooldown + a confirmed chain sweep
          </div>
        )}
      </div>
    </div>
  );
};

export default TradingSetupPanel;
